import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import DashboardLayout from "../layout/DashboardLayout";
import ChatMetricsPanel from "./ChatMetricsPanel";
import "./ChatPage.css";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

interface Session {
  id: number;
  session_id: string;
  guest_name: string | null;
  guest_phone: string | null;
  booking_id: string | null;
  source: string;
  status: "open" | "resolved" | "closed" | "needs_attention";
  unread: number;
  ai_enabled: boolean | null;
  handoff_reason: string | null;
  handoff_briefing: string | null;
  handoff_briefing_at: string | null;
  created_at: string;
  updated_at: string;
  last_message: string | null;
  last_message_role: string | null;
  last_message_at: string | null;
  room_number: string | null;
  guest_status: "inhouse" | "arriving" | null;
  tags: string[];
}

interface Message {
  id: number;
  session_id: string;
  role: "user" | "assistant" | "staff" | "note";
  content: string;
  sent_by: string | null;
  created_at: string;
  wa_status?: string | null;
  wa_message_id?: string | null;
}

interface MediaContent {
  type: "image" | "audio" | "video" | "document";
  url: string;
  caption?: string;
}

// Bracket pattern for legacy/gateway placeholders like "[Media message]", "[image]", "[audio]"
const BRACKET_MEDIA_RE = /^\[(media message|image|photo|audio|voice|voice ?note|video|document|file|sticker|gif)\]$/i;

function parseMediaContent(content: string): MediaContent | null {
  if (content.startsWith("{")) {
    try {
      const parsed = JSON.parse(content);
      if (parsed.type && parsed.url) return {
        type: parsed.type,
        url: parsed.url,
        caption: parsed.caption || parsed.description || parsed.transcription || undefined,
      } as MediaContent;
    } catch { /* not JSON */ }
  }
  // Handle bracket placeholders where no URL was forwarded
  const m = content.trim().match(BRACKET_MEDIA_RE);
  if (m) {
    const raw = m[1].toLowerCase();
    const type: MediaContent["type"] =
      raw.includes("image") || raw.includes("photo") || raw.includes("sticker") || raw.includes("gif") ? "image"
      : raw.includes("audio") || raw.includes("voice") ? "audio"
      : raw.includes("video") ? "video"
      : "document";
    return { type, url: "", caption: "" };
  }
  return null;
}

const MEDIA_LABELS: Record<string, string> = { audio: "Voice note", image: "Photo", video: "Video", document: "File" };

function MediaIcon({ type }: { type: string }) {
  const common = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.7, width: 16, height: 16 };
  if (type === "image") return <svg {...common}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>;
  if (type === "audio") return <svg {...common}><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>;
  if (type === "video") return <svg {...common}><rect x="2" y="5" width="14" height="14" rx="2"/><polyline points="16 9 22 6 22 18 16 15"/></svg>;
  return <svg {...common}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>;
}

// ── Hotel Facts key ─────────────────────────────────────────────────────────────
const HOTEL_FACTS_KEY = "ai2_hotel_facts_v1";

// ── Tags ───────────────────────────────────────────────────────────────────────
const PRESET_TAGS = ["VIP", "Complaint", "Maintenance", "Billing", "Special Request", "Follow-up"];

// ── Translation helper ─────────────────────────────────────────────────────────
function hasNonLatin(text: string): boolean {
  return /[؀-ۿ֐-׿฀-๿一-鿿぀-ヿ가-힯]/.test(text);
}

function MediaUnavailable({ type }: { type: string }) {
  return (
    <div className="chat-media-unavailable">
      <MediaIcon type={type} />
      <span className="chat-media-unavailable-label">{MEDIA_LABELS[type] ?? "Media"}</span>
      <span className="chat-media-unavailable-sub">unavailable</span>
    </div>
  );
}

function ChatImage({ media, onImageClick }: { media: MediaContent; onImageClick?: (url: string, caption?: string) => void }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <MediaUnavailable type="image" />;
  return (
    <img
      src={media.url}
      alt="Photo"
      onError={() => setFailed(true)}
      onClick={() => onImageClick?.(media.url, media.caption)}
      style={{ maxWidth: "min(260px, calc(75vw - 24px))", maxHeight: 200, width: "100%", borderRadius: 8, display: "block", objectFit: "cover", cursor: "pointer" }}
    />
  );
}

const WAVE = [4,7,10,6,9,5,8,11,6,9,4,7,10,8,5,9,6,11,7,9,5,7,10,6,8,4,7,9,5,8];

function VoicePlayer({ url, caption }: { url: string; caption?: string }) {
  const ref = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying]   = useState(false);
  const [current, setCurrent]   = useState(0);
  const [dur, setDur]           = useState(0);
  const [failed, setFailed]     = useState(false);

  const toggle = () => {
    const a = ref.current; if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play().catch(() => {}); setPlaying(true); }
  };

  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    const a = ref.current; if (!a || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    a.currentTime = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)) * dur;
  };

  const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
  const pct = dur > 0 ? current / dur : 0;

  if (failed) return <MediaUnavailable type="audio" />;

  return (
    <div className="voice-player">
      <audio
        ref={ref}
        src={url}
        preload="metadata"
        onTimeUpdate={() => setCurrent(ref.current?.currentTime ?? 0)}
        onLoadedMetadata={() => setDur(ref.current?.duration ?? 0)}
        onEnded={() => { setPlaying(false); setCurrent(0); }}
        onError={() => setFailed(true)}
      />
      <button className={`voice-play-btn${playing ? " playing" : ""}`} onClick={toggle}>
        {playing
          ? <svg viewBox="0 0 24 24" fill="currentColor" width="13" height="13"><rect x="5" y="4" width="4" height="16" rx="1"/><rect x="15" y="4" width="4" height="16" rx="1"/></svg>
          : <svg viewBox="0 0 24 24" fill="currentColor" width="13" height="13"><polygon points="6 3 20 12 6 21"/></svg>
        }
      </button>
      <div className="voice-body">
        <div className="voice-wave" onClick={seek}>
          <div className="voice-wave-track">
            {WAVE.map((h, i) => <div key={i} className={`voice-bar${playing ? " anim" : ""}`} style={{ height: h, animationDelay: `${(i % 6) * 0.08}s` }} />)}
          </div>
          <div className="voice-wave-played" style={{ width: `${pct * 100}%` }}>
            <div className="voice-wave-track">
              {WAVE.map((h, i) => <div key={i} className="voice-bar played" style={{ height: h }} />)}
            </div>
          </div>
        </div>
        <span className="voice-dur">{current > 0 ? fmt(current) : (dur > 0 ? fmt(dur) : "0:00")}</span>
      </div>
      {caption && <span className="voice-caption">{caption}</span>}
    </div>
  );
}

function MessageMedia({ media, onImageClick }: { media: MediaContent; onImageClick?: (url: string, caption?: string) => void }) {
  // No URL = gateway sent a placeholder like "[Media message]" without a URL
  if (!media.url) return <MediaUnavailable type={media.type} />;

  if (media.type === "audio") {
    return <VoicePlayer url={media.url} caption={media.caption} />;
  }
  if (media.type === "image") {
    return <ChatImage media={media} onImageClick={onImageClick} />;
  }
  if (media.type === "video") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <video controls style={{ maxWidth: 260, maxHeight: 180, borderRadius: 8 }} preload="metadata">
          <source src={media.url} />
        </video>
        {media.caption && <span style={{ fontSize: 12, opacity: 0.7 }}>{media.caption}</span>}
      </div>
    );
  }
  return (
    <a href={media.url} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
      <MediaIcon type="document" /> {media.caption || "Download file"}
    </a>
  );
}

function AlbumCell({ img, onImageClick }: { img: { url: string; caption?: string }; onImageClick?: (url: string, caption?: string) => void }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div className="chat-album-fallback">
        <MediaIcon type="image" />
      </div>
    );
  }
  return <img src={img.url} alt="" className="chat-album-img" onError={() => setFailed(true)} />;
}

function AlbumGrid({ msgs, onImageClick }: { msgs: { url: string; caption?: string }[]; onImageClick?: (url: string, caption?: string) => void }) {
  const count = Math.min(msgs.length, 4);
  const extra = msgs.length - 4;
  return (
    <div className={`chat-album chat-album-${count}`}>
      {msgs.slice(0, count).map((img, i) => (
        <div key={i} className="chat-album-cell" onClick={() => onImageClick?.(img.url, img.caption)}>
          <AlbumCell img={img} onImageClick={onImageClick} />
          {i === count - 1 && extra > 0 && (
            <div className="chat-album-more">+{extra + 1}</div>
          )}
        </div>
      ))}
    </div>
  );
}

// Highlight occurrences of `query` inside `text` for search
function highlightText(text: string, query: string): React.ReactNode {
  if (!query.trim()) return text;
  const q = query.trim();
  const parts: React.ReactNode[] = [];
  const lower = text.toLowerCase();
  const lq = q.toLowerCase();
  let idx = 0, from = 0, key = 0;
  while ((idx = lower.indexOf(lq, from)) !== -1) {
    if (idx > from) parts.push(text.slice(from, idx));
    parts.push(<mark key={key++} className="chat-search-hl">{text.slice(idx, idx + q.length)}</mark>);
    from = idx + q.length;
  }
  if (from < text.length) parts.push(text.slice(from));
  return parts;
}

interface BookingRow {
  bookingid: string;
  guestname: string;
  phonenumber: string;
  checkin: string;
  checkout: string;
  status: string;
  roomnumber: string | null;
  nights: number;
  total: number;
  createdat: string | null;
  booking_type: string | null;
}
interface GuestInfo {
  session: Session | null;
  booking: BookingRow | null;
  allBookings: BookingRow[];
  stats: { totalStays: number; totalNights: number; totalSpent: number; firstVisit: string | null };
  notes?: { id: string; text: string; author: string; created_at: string }[];
}

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return "now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

function fmtDate(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString())     return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: d.getFullYear() !== today.getFullYear() ? "numeric" : undefined });
}

function sameDay(a: string, b: string) {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

function previewText(content: string | null, role: string | null) {
  if (!content) return "No messages yet";
  const prefix = role === "assistant" ? "AI: " : role === "staff" ? "You: " : "";
  const media = parseMediaContent(content);
  if (media) {
    const labels: Record<string, string> = { audio: "Voice message", image: "Photo", video: "Video", document: "File" };
    return prefix + (labels[media.type] || "Media");
  }
  const text = content.replace(/\n/g, " ").slice(0, 48);
  return prefix + (content.length > 48 ? text + "…" : text);
}

function waitingInfo(s: { last_message_role: string | null; updated_at: string; status: string }) {
  if (s.last_message_role !== "user" || s.status !== "open") return null;
  const mins = Math.round((Date.now() - new Date(s.updated_at).getTime()) / 60000);
  if (mins < 5) return null;
  return { mins, level: mins >= 20 ? "red" : "amber" } as const;
}

function fmtWait(mins: number) {
  if (mins < 60) return `${mins}m waiting`;
  return `${Math.round(mins / 60)}h waiting`;
}

function avatarColor(name: string | null): { bg: string; fg: string } {
  const palette = [
    { bg: "rgba(239,68,68,0.22)",   fg: "#fca5a5" },
    { bg: "rgba(59,130,246,0.22)",  fg: "#93c5fd" },
    { bg: "rgba(245,158,11,0.22)",  fg: "#fcd34d" },
    { bg: "rgba(139,92,246,0.22)",  fg: "#c4b5fd" },
    { bg: "rgba(16,185,129,0.22)",  fg: "#6ee7b7" },
    { bg: "rgba(236,72,153,0.22)",  fg: "#f9a8d4" },
    { bg: "rgba(249,115,22,0.22)",  fg: "#fdba74" },
    { bg: "rgba(20,184,166,0.22)",  fg: "#5eead4" },
  ];
  if (!name) return palette[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h << 5) - h + name.charCodeAt(i);
  return palette[Math.abs(h) % palette.length];
}

function WaIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width="9" height="9">
      <path d="M12 2C6.48 2 2 6.48 2 12c0 1.85.5 3.58 1.38 5.07L2 22l5.07-1.35C8.47 21.52 10.21 22 12 22c5.52 0 10-4.48 10-10S17.52 2 12 2zm3.18 14.26c-.26.74-1.5 1.37-2.06 1.45-.55.08-1.24.11-2-.24-2.44-.99-4.02-3.46-4.14-3.62-.12-.16-.97-1.29-.97-2.45 0-1.17.61-1.74.83-1.98.21-.23.47-.29.63-.29.16 0 .32 0 .45.01.14.01.33-.05.52.4.19.45.63 1.54.69 1.65.06.11.1.24.02.38-.08.14-.12.23-.23.35-.11.12-.24.27-.34.36-.11.09-.23.2-.1.4.13.2.58.89 1.24 1.43.85.72 1.57.94 1.8 1.04.22.1.35.08.48-.05.13-.14.54-.63.69-.85.14-.21.29-.18.49-.1.19.08 1.24.58 1.46.69.22.1.36.16.41.25.05.08.05.5-.2 1.18z"/>
    </svg>
  );
}

interface WaTemplate { name: string; language: string; bodyText: string; headerText: string | null; footerText: string | null; variables: string[]; }
interface BlastResult { phone: string; status: "sent" | "failed" | "skipped"; reason?: string; }

export default function ChatPage() {
  const location = useLocation();
  const autoOpenRef = useRef(false);
  const [sessions, setSessions]         = useState<Session[]>([]);
  const [pinnedIds, setPinnedIds]       = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem("chat_pinned_v1") || "[]")); }
    catch { return new Set(); }
  });
  const [active, setActive]             = useState<Session | null>(null);
  const [messages, setMessages]         = useState<Message[]>([]);
  const [reply, setReply]               = useState("");
  const [sending, setSending]           = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [loadingMsgs, setLoadingMsgs]   = useState(false);
  const [filter, setFilter]             = useState<"all" | "open" | "resolved" | "needs_attention" | "needs_reply">("all");
  const [botEnabled, setBotEnabled]     = useState(false);
  const [togglingBot, setTogglingBot]   = useState(false);
  const [search, setSearch]             = useState("");
  const [showInfo, setShowInfo]         = useState(() => window.innerWidth > 768);
  const [guestInfo, setGuestInfo]       = useState<GuestInfo | null>(null);
  const [loadingInfo, setLoadingInfo]   = useState(false);
  const [showFilterDd, setShowFilterDd] = useState(false);
  const [view, setView]                 = useState<"chat" | "metrics" | "contacts">("chat");
  const [contacts, setContacts]         = useState<Array<{ id: number; name: string; phoneNumber: string; email?: string }>>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactSearch, setContactSearch] = useState("");
  const [theme, setTheme]               = useState<"dark" | "light">(() => (localStorage.getItem("chat_theme") as "dark" | "light") || "dark");
  const toggleTheme = () => setTheme(t => { const n = t === "dark" ? "light" : "dark"; localStorage.setItem("chat_theme", n); return n; });

  // ── Feature 1: Command shortcut panels ────────────────────────────────────
  const [cmdPanel, setCmdPanel]         = useState<"guest" | "hotel" | "template" | null>(null);
  const [cmdSearch, setCmdSearch]       = useState("");
  const [cmdManage, setCmdManage]       = useState(false);
  // Snippets are loaded from the database (hotel policies + FAQs) — see the effect below.
  // localStorage is only an offline cache so the panel isn't empty on first paint.
  const [hotelFacts, setHotelFacts]     = useState<{id:string;title:string;text:string}[]>(() => {
    try { return JSON.parse(localStorage.getItem(HOTEL_FACTS_KEY) || "null") || []; } catch { return []; }
  });
  const [newFactTitle, setNewFactTitle] = useState("");
  const [newFactText, setNewFactText]   = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<WaTemplate | null>(null);
  const cmdPanelRef                     = useRef<HTMLDivElement>(null);

  // ── Feature: Staff Notes in Info Panel ────────────────────────────────────
  const [newNoteText, setNewNoteText]   = useState("");

  // ── Feature 2: Thread Search ───────────────────────────────────────────────
  const [showSearch, setShowSearch]     = useState(false);
  const [threadSearch, setThreadSearch] = useState("");

  // ── Feature 3: Image Sending ──────────────────────────────────────────────
  const [imageFile, setImageFile]       = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage]   = useState(false);
  const fileInputRef                    = useRef<HTMLInputElement>(null);

  // ── Feature: Translation ───────────────────────────────────────────────────
  const [translations, setTranslations]       = useState<Record<string, string>>({});
  const [translatingIds, setTranslatingIds]   = useState<Set<string>>(new Set());

  // ── Feature: Tags ──────────────────────────────────────────────────────────
  const [showTagPicker, setShowTagPicker]     = useState(false);
  const tagPickerRef                          = useRef<HTMLDivElement>(null);

  // ── New Chat modal ──
  const [showNewChat, setShowNewChat]       = useState(false);
  const [ncSearch, setNcSearch]             = useState("");
  const [ncGuests, setNcGuests]             = useState<Array<{ id: number; name: string; phoneNumber: string; email?: string; isVIP?: boolean }>>([]);
  const [ncLoading, setNcLoading]           = useState(false);
  const [ncStarting, setNcStarting]         = useState<string | null>(null);

  // ── Rename guest ──
  const [renaming, setRenaming]     = useState(false);
  const [renameVal, setRenameVal]   = useState("");
  const renameInputRef              = useRef<HTMLInputElement>(null);

  const startRename = () => {
    setRenameVal(active?.guest_name || "");
    setRenaming(true);
    setTimeout(() => renameInputRef.current?.focus(), 30);
  };

  const commitRename = async () => {
    if (!active) return;
    const name = renameVal.trim() || null;
    try {
      const { data } = await axios.patch(`${API}/api/chat/sessions/${active.session_id}`, { guest_name: name });
      setSessions(prev => prev.map(s => s.session_id === active.session_id ? { ...s, guest_name: name } : s));
      setActive(prev => prev ? { ...prev, guest_name: name } : prev);
    } catch {}
    setRenaming(false);
  };

  // ── Blast Template modal ──
  const [showBlast, setShowBlast]               = useState(false);
  const [blastTab, setBlastTab]                 = useState<"all" | "new" | "waiting">("all");
  const [blastTemplates, setBlastTemplates]     = useState<WaTemplate[]>([]);
  const [blastTplLoading, setBlastTplLoading]   = useState(false);
  const [blastTplError, setBlastTplError]       = useState<string | null>(null);
  const [blastSelected, setBlastSelected]       = useState<WaTemplate | null>(null);
  const [blastVars, setBlastVars]               = useState<Record<string, string>>({});
  const [blastGuests, setBlastGuests]           = useState<Array<{ name: string; phone: string }>>([]);
  const [blastAllGuests, setBlastAllGuests]     = useState<Array<{ name: string; phone: string }>>([]);
  const [blastNameOverrides, setBlastNameOverrides] = useState<Record<string, string>>({});
  const [blastGuestSel, setBlastGuestSel]       = useState<Set<string>>(new Set());
  const [blastGuestSearch, setBlastGuestSearch] = useState("");
  const [blastSending, setBlastSending]         = useState(false);
  const [blastResults, setBlastResults]         = useState<BlastResult[] | null>(null);

  const openBlastModal = async () => {
    setShowBlast(true);
    setBlastSelected(null);
    setBlastVars({});
    setBlastResults(null);
    setBlastGuestSel(new Set());
    setBlastGuestSearch("");
    setBlastNameOverrides({});
    setBlastTplError(null);
    setBlastTplLoading(true);
    try {
      const [tplRes, guestRes] = await Promise.all([
        axios.get(`${API}/api/chat/templates`),
        axios.get(`${API}/api/dashboard/guests`),
      ]);
      setBlastTemplates(tplRes.data?.templates || []);
      const rawGuests: Array<{ id: number; name: string; phoneNumber: string }> = guestRes.data || [];
      const mapped = rawGuests
        .filter(g => g.phoneNumber)
        .map(g => ({ name: g.name || "", phone: String(g.phoneNumber).replace(/\D/g, "") }))
        .filter(g => g.phone);
      setBlastAllGuests(mapped);
      const sessionPhones = new Set(sessions.map(s => s.session_id));
      setBlastGuests(mapped.filter(g => !sessionPhones.has(g.phone)));
    } catch (e: any) {
      setBlastTplError(e?.response?.data?.error || e.message || "Failed to load");
    } finally {
      setBlastTplLoading(false);
    }
  };

  const blastGuestsForTab =
    blastTab === "all"     ? blastAllGuests :
    blastTab === "new"     ? blastGuests :
    sessions
      .filter(s => s.last_message_role === "user" && s.status === "open" &&
        (Date.now() - new Date(s.updated_at).getTime()) > 24 * 60 * 60 * 1000)
      .map(s => ({ name: s.guest_name || s.session_id, phone: s.session_id }));

  const blastGuestsFiltered = blastGuestsForTab.filter(g =>
    (g.name || "").toLowerCase().includes(blastGuestSearch.toLowerCase()) ||
    g.phone.includes(blastGuestSearch)
  );

  const toggleBlastGuest = (phone: string) =>
    setBlastGuestSel(prev => { const n = new Set(prev); n.has(phone) ? n.delete(phone) : n.add(phone); return n; });

  const selectAllBlast = () => setBlastGuestSel(new Set(blastGuestsFiltered.map(g => g.phone)));
  const clearBlastSel  = () => setBlastGuestSel(new Set());

  const sendBlast = async () => {
    if (!blastSelected || blastGuestSel.size === 0) return;
    setBlastSending(true);
    setBlastResults(null);
    try {
      const recipients = blastGuestsForTab
        .filter(g => blastGuestSel.has(g.phone))
        .map(g => ({ ...g, name: blastNameOverrides[g.phone] || g.name || "" }));
      const { data } = await axios.post(`${API}/api/chat/blast-template`, {
        templateName: blastSelected.name,
        language: blastSelected.language,
        variables: blastVars,
        recipients,
        bodyText: blastSelected.bodyText,
      });
      setBlastResults(data.results || []);
      await fetchSessions();
    } catch (e: any) {
      setBlastTplError(e?.response?.data?.error || e.message);
    } finally {
      setBlastSending(false);
    }
  };

  const messagesEndRef  = useRef<HTMLDivElement>(null);
  const messagesBoxRef  = useRef<HTMLDivElement>(null);
  const pollRef         = useRef<ReturnType<typeof setInterval> | null>(null);
  const userScrolledUp  = useRef(false);
  const justOpenedRef   = useRef(false);
  const lbTouchX        = useRef(0);
  const filterDdRef     = useRef<HTMLDivElement>(null);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  // Track unread per session (only non-active sessions are reliable — active session is reset by fetchMessages)
  const prevUnreadRef    = useRef<Map<string, number>>(new Map());
  const prevAttentionRef = useRef<Set<string>>(new Set());
  const chatInitRef      = useRef(true);
  const activeRef        = useRef<Session | null>(null);   // always current active, without effect dep
  const prevMsgCountRef  = useRef<Map<string, number>>(new Map()); // user msg count for active session
  const [flashIds, setFlashIds] = useState<Set<string>>(new Set());
  const [lightbox, setLightbox] = useState<{ images: { url: string; caption?: string }[]; idx: number } | null>(null);

  const staffName = (() => {
    try { const d: any = jwtDecode(localStorage.getItem("ai2_token") || ""); return d.name || "Staff"; }
    catch { return "Staff"; }
  })();

  // Guest snippets for the / command panel
  const guestSnippets = useMemo(() => {
    const b = guestInfo?.booking;
    const fmt = (d?: string | null) => d ? new Date(d + "T12:00:00").toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" }) : null;
    const snippets: {title:string; text:string}[] = [];
    if (active?.guest_name) snippets.push({ title: "Guest name", text: active.guest_name });
    if (b?.checkin)     snippets.push({ title: "Check-in date",  text: `Your check-in date is ${fmt(b.checkin)}.` });
    if (b?.checkout)    snippets.push({ title: "Check-out date", text: `Your check-out date is ${fmt(b.checkout)}.` });
    if (b?.roomnumber)  snippets.push({ title: "Room number",    text: `Your room number is ${b.roomnumber}.` });
    if (b?.bookingid)   snippets.push({ title: "Booking ref",    text: `Your booking reference is #${String(b.bookingid).slice(-6)}.` });
    if (b?.nights)      snippets.push({ title: "Nights",         text: `Your stay is ${b.nights} night${b.nights !== 1 ? "s" : ""}.` });
    if (b?.total && b.total > 0) snippets.push({ title: "Total", text: `Your booking total is ${b.total.toLocaleString()}.` });
    return snippets;
  }, [guestInfo, active]);

  const fetchContacts = async () => {
    setContactsLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/dashboard/guests`);
      setContacts(data || []);
    } catch { }
    finally { setContactsLoading(false); }
  };

  const fetchSessions = async () => {
    try {
      const { data } = await axios.get(`${API}/api/chat/sessions`);
      setSessions(data || []);
    } catch { }
    finally { setLoadingSessions(false); }
  };

  const deleteSession = async (sessionId: string) => {
    try {
      await axios.delete(`${API}/api/chat/sessions/${sessionId}`);
      setSessions(prev => prev.filter(s => s.session_id !== sessionId));
      if (active?.session_id === sessionId) setActive(null);
    } catch { }
  };

  const togglePin = (sessionId: string) => {
    setPinnedIds(prev => {
      const next = new Set(prev);
      if (next.has(sessionId)) next.delete(sessionId); else next.add(sessionId);
      localStorage.setItem("chat_pinned_v1", JSON.stringify([...next]));
      return next;
    });
  };

  const fetchMessages = async (sessionId: string) => {
    setLoadingMsgs(true);
    try {
      const { data } = await axios.get(`${API}/api/chat/sessions/${sessionId}/messages`);
      const raw: Message[] = data || [];
      const seen = new Set<string>();
      const deduped = raw.filter(m => {
        const key = m.id ? String(m.id) : `${m.role}|${m.content}|${m.created_at}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setMessages(deduped);
      setSessions(prev => prev.map(s => s.session_id === sessionId ? { ...s, unread: 0 } : s));

      // Detect new user messages in the active session by counting
      // (unread-based detection doesn't work for active session since fetchMessages resets DB unread)
      if (!chatInitRef.current) {
        const userCount = deduped.filter(m => m.role === "user").length;
        const prev = prevMsgCountRef.current.get(sessionId) ?? -1;
        if (prev !== -1 && userCount > prev) {
          flashTitle(false);
          try {
            const ctx = new AudioContext();
            ctx.resume().then(() => {
              const osc = ctx.createOscillator();
              const gain = ctx.createGain();
              osc.connect(gain);
              gain.connect(ctx.destination);
              osc.type = "sine";
              osc.frequency.setValueAtTime(900, ctx.currentTime);
              osc.frequency.exponentialRampToValueAtTime(520, ctx.currentTime + 0.25);
              gain.gain.setValueAtTime(0.18, ctx.currentTime);
              gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
              osc.start(ctx.currentTime);
              osc.stop(ctx.currentTime + 0.3);
            });
          } catch { }
        }
        prevMsgCountRef.current.set(sessionId, userCount);
      } else {
        // On init, just record the count without playing sound
        const userCount = deduped.filter(m => m.role === "user").length;
        prevMsgCountRef.current.set(sessionId, userCount);
      }
    } catch { }
    finally { setLoadingMsgs(false); }
  };

  const fetchGuestInfo = async (sessionId: string) => {
    setLoadingInfo(true);
    try {
      const { data } = await axios.get(`${API}/api/chat/sessions/${sessionId}/info`);
      setGuestInfo(data);
    } catch { }
    finally { setLoadingInfo(false); }
  };

  const fetchBotStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/api/chat/bot-status`);
      setBotEnabled(data.chat_enabled);
    } catch { }
  };

  const toggleBot = async () => {
    if (togglingBot) return;
    setTogglingBot(true);
    try {
      const { data } = await axios.patch(`${API}/api/chat/bot-toggle`, { enabled: !botEnabled });
      setBotEnabled(data.chat_enabled);
    } catch { }
    finally { setTogglingBot(false); }
  };

  // Persist hotel facts to localStorage
  useEffect(() => {
    localStorage.setItem(HOTEL_FACTS_KEY, JSON.stringify(hotelFacts));
  }, [hotelFacts]);

  // Load live-chat snippets from the DATABASE: hotel policies + FAQs (kept in sync with the bot)
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("ai2_token") || "";
        let hotelId = "";
        try { const d: any = jwtDecode(token); hotelId = d.hotelId || ""; } catch { /* */ }
        const [sRes, fRes] = await Promise.all([
          hotelId ? axios.get(`${API}/api/hotel/settings/${hotelId}`) : Promise.resolve({ data: {} } as any),
          axios.get(`${API}/api/settings/faqs`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] } as any)),
        ]);
        const s = sRes.data || {};
        const facts: { id: string; title: string; text: string }[] = [];
        if (s.pets_policy)    facts.push({ id: "cfg-pets",   title: "Pet policy",     text: `Pets: ${s.pets_policy}.` });
        if (s.check_in)       facts.push({ id: "cfg-cin",    title: "Check-in time",  text: `Check-in time is ${s.check_in}.` });
        if (s.check_out)      facts.push({ id: "cfg-cout",   title: "Check-out time", text: `Check-out time is ${s.check_out}.` });
        if (s.refund_policy)  facts.push({ id: "cfg-refund", title: "Cancellation",   text: String(s.refund_policy) });
        if (s.smoking_policy) facts.push({ id: "cfg-smoke",  title: "Smoking policy", text: `Smoking: ${s.smoking_policy}.` });
        (Array.isArray(fRes.data) ? fRes.data : []).forEach((f: any) => {
          const txt = f.faq_content || f.faq_summary;
          if (txt) facts.push({ id: `faq-${f.id}`, title: f.faq_summary || "FAQ", text: txt });
        });
        if (facts.length) setHotelFacts(facts);
      } catch { /* keep cached */ }
    })();
  }, []);

  // Close cmd panel on outside click
  useEffect(() => {
    if (!cmdPanel) return;
    const handler = (e: MouseEvent) => {
      if (cmdPanelRef.current && !cmdPanelRef.current.contains(e.target as Node)) {
        setCmdPanel(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [cmdPanel]);

  // Close tag picker on outside click
  useEffect(() => {
    if (!showTagPicker) return;
    const handler = (e: MouseEvent) => {
      if (tagPickerRef.current && !tagPickerRef.current.contains(e.target as Node)) {
        setShowTagPicker(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showTagPicker]);

  useEffect(() => {
    fetchSessions();
    fetchBotStatus();
    const id = setInterval(fetchSessions, 8000);
    return () => clearInterval(id);
  }, []);

  // Keep activeRef in sync so the sessions effect can read current active without dep
  useEffect(() => { activeRef.current = active; }, [active]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setLightbox(null); return; }
      if (e.key === "ArrowLeft")  setLightbox(l => l && l.idx > 0 ? { ...l, idx: l.idx - 1 } : l);
      if (e.key === "ArrowRight") setLightbox(l => l && l.idx < l.images.length - 1 ? { ...l, idx: l.idx + 1 } : l);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [lightbox]);

  // All images in the current conversation for carousel
  const chatImages = useMemo(() => messages.reduce<{ url: string; caption?: string }[]>((acc, m) => {
    const media = parseMediaContent(m.content);
    if (media?.type === "image" && media.url) acc.push({ url: media.url, caption: media.caption });
    return acc;
  }, []), [messages]);

  const openLightbox = (url: string, caption?: string) => {
    const idx = chatImages.findIndex(img => img.url === url);
    setLightbox({ images: chatImages, idx: Math.max(0, idx) });
  };

  // Group consecutive same-sender images (within 5 min) into albums
  type DItem =
    | { kind: "msg";   msg: Message }
    | { kind: "album"; msgs: Message[]; role: string; sent_by: string | null; created_at: string };
  const displayItems = useMemo<DItem[]>(() => {
    const items: DItem[] = [];
    const consumed = new Set<number>();

    // Source messages: filter by thread search if active
    const srcMessages = messages;

    for (let i = 0; i < srcMessages.length; i++) {
      if (consumed.has(i)) continue;
      const msg = srcMessages[i];
      const media = parseMediaContent(msg.content);
      // Notes are always individual — never grouped into albums
      if (msg.role === "note") {
        items.push({ kind: "msg", msg });
        continue;
      }
      if (media?.type === "image" && media.url) {
        const group = [msg];
        for (let j = i + 1; j < srcMessages.length; j++) {
          const next = srcMessages[j];
          if (next.role === "note") break; // don't group across notes
          const nm = parseMediaContent(next.content);
          if (!nm || nm.type !== "image" || !nm.url) break;
          if (next.role !== msg.role) break;
          if (msg.role === "staff" && next.sent_by !== msg.sent_by) break;
          if (Math.abs(new Date(next.created_at).getTime() - new Date(msg.created_at).getTime()) > 5 * 60 * 1000) break;
          group.push(next);
          consumed.add(j);
        }
        if (group.length > 1) {
          items.push({ kind: "album", msgs: group, role: msg.role, sent_by: msg.sent_by, created_at: msg.created_at });
        } else {
          items.push({ kind: "msg", msg });
        }
      } else {
        items.push({ kind: "msg", msg });
      }
    }

    // Search no longer filters — all messages stay visible, matches are highlighted/navigated
    return items;
  }, [messages]);

  // Search match navigation — list of matching message ids in order
  const searchMatches = useMemo<number[]>(() => {
    if (!threadSearch.trim()) return [];
    const q = threadSearch.toLowerCase();
    return messages
      .filter(m => m.role !== "note" && m.content && !m.content.startsWith('{"type":"image"') && m.content.toLowerCase().includes(q))
      .map(m => m.id);
  }, [messages, threadSearch]);

  const [searchIdx, setSearchIdx] = useState(0);
  // Reset to first match whenever the query changes
  useEffect(() => { setSearchIdx(0); }, [threadSearch]);
  // Scroll the current match into view
  useEffect(() => {
    if (!threadSearch.trim() || searchMatches.length === 0) return;
    const id = searchMatches[Math.min(searchIdx, searchMatches.length - 1)];
    const el = document.getElementById(`msg-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [searchIdx, searchMatches, threadSearch]);

  const gotoMatch = (dir: 1 | -1) => {
    if (searchMatches.length === 0) return;
    setSearchIdx(prev => (prev + dir + searchMatches.length) % searchMatches.length);
  };

  // Auto-open a session when navigated from Guest page with openWaId state
  useEffect(() => {
    const openWaId = (location.state as any)?.openWaId;
    if (!openWaId || autoOpenRef.current || sessions.length === 0) return;
    const target = sessions.find(s =>
      s.session_id === openWaId ||
      s.guest_phone === openWaId ||
      s.session_id === String(openWaId) ||
      s.guest_phone === String(openWaId)
    );
    if (target) {
      autoOpenRef.current = true;
      setActive(target);
    }
  }, [sessions, location.state]);

  // Universal title flash — works on iOS Safari, Chrome mobile, all browsers
  const titleFlashRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const flashTitle = (urgent: boolean) => {
    if (titleFlashRef.current) clearInterval(titleFlashRef.current);
    const base = document.title.replace(/^\(.*?\) /, "").replace(/^🔴 /, "");
    const alert = urgent ? "🔴 HANDOFF" : "💬 New message";
    let toggle = true;
    titleFlashRef.current = setInterval(() => {
      document.title = toggle ? `${alert} | ${base}` : base;
      toggle = !toggle;
    }, 900);
    setTimeout(() => {
      if (titleFlashRef.current) { clearInterval(titleFlashRef.current); titleFlashRef.current = null; }
      document.title = base;
    }, 45_000);
  };

  // Sound + browser notification on new messages or handoffs
  // Strategy:
  //   - Non-active sessions: compare DB unread count. fetchMessages only runs for the active session,
  //     so non-active sessions' DB unread is never reset until the user opens them. Reliable.
  //   - Active session: handled in fetchMessages via user-message count (see below).
  //   - Handoffs: always trigger on status → "needs_attention" transition.
  useEffect(() => {
    if (sessions.length === 0) return;

    if (chatInitRef.current) {
      sessions.forEach(s => {
        prevUnreadRef.current.set(s.session_id, s.unread || 0);
        if (s.status === "needs_attention") prevAttentionRef.current.add(s.session_id);
      });
      chatInitRef.current = false;
      return;
    }

    const newMsgSessions: Session[]     = [];
    const newHandoffSessions: Session[] = [];
    const activeId = activeRef.current?.session_id;

    sessions.forEach(s => {
      const prevUnread   = prevUnreadRef.current.get(s.session_id) ?? 0;
      const wasAttention = prevAttentionRef.current.has(s.session_id);
      if (s.status === "needs_attention" && !wasAttention) {
        newHandoffSessions.push(s);
      } else if (s.session_id !== activeId && (s.unread || 0) > prevUnread) {
        // Non-active session got new user message(s)
        newMsgSessions.push(s);
      }
    });

    if (newHandoffSessions.length > 0) {
      flashTitle(true);
      try {
        const ctx = new AudioContext();
        ctx.resume().then(() => {
          const gain = ctx.createGain();
          gain.connect(ctx.destination);
          [0, 0.18, 0.36].forEach(offset => {
            const osc = ctx.createOscillator();
            osc.connect(gain);
            osc.type = "square";
            osc.frequency.setValueAtTime(880, ctx.currentTime + offset);
            gain.gain.setValueAtTime(0.12, ctx.currentTime + offset);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + offset + 0.14);
            osc.start(ctx.currentTime + offset);
            osc.stop(ctx.currentTime + offset + 0.15);
          });
        });
      } catch { }
      if ("Notification" in window && Notification.permission === "granted") {
        newHandoffSessions.forEach(s => {
          try {
            const n = new Notification("⚠️ Handoff Alert", {
              body: `${s.guest_name || "Guest"} needs assistance${s.handoff_reason ? ": " + s.handoff_reason.slice(0, 80) : ""}`,
              icon: "/Logo.png",
              tag: `handoff-${s.session_id}`,
              requireInteraction: true,
            });
            n.onclick = () => { window.focus(); n.close(); };
          } catch { }
        });
      }
    } else if (newMsgSessions.length > 0) {
      flashTitle(false);
      try {
        const ctx = new AudioContext();
        ctx.resume().then(() => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = "sine";
          osc.frequency.setValueAtTime(900, ctx.currentTime);
          osc.frequency.exponentialRampToValueAtTime(520, ctx.currentTime + 0.25);
          gain.gain.setValueAtTime(0.18, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.3);
        });
      } catch { }
      if ("Notification" in window && Notification.permission === "granted") {
        newMsgSessions.forEach(s => {
          try {
            const preview = s.last_message?.slice(0, 100) || "New message";
            const n = new Notification(`💬 ${s.guest_name || "Guest"}`, {
              body: preview,
              icon: "/Logo.png",
              tag: `msg-${s.session_id}`,
              silent: true,
            });
            if (!document.hasFocus()) setTimeout(() => n.close(), 6000);
            n.onclick = () => { window.focus(); n.close(); };
          } catch { }
        });
      }
    }

    if (newMsgSessions.length > 0 || newHandoffSessions.length > 0) {
      const flashSet = new Set([...newMsgSessions, ...newHandoffSessions].map(s => s.session_id));
      setFlashIds(flashSet);
      setTimeout(() => setFlashIds(new Set()), 2000);
    }

    sessions.forEach(s => {
      prevUnreadRef.current.set(s.session_id, s.unread || 0);
      if (s.status === "needs_attention") prevAttentionRef.current.add(s.session_id);
      else prevAttentionRef.current.delete(s.session_id);
    });
  }, [sessions]);

  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (!active) return;
    fetchMessages(active.session_id);
    pollRef.current = setInterval(() => fetchMessages(active.session_id), 4000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [active?.session_id]);

  useEffect(() => {
    const el = messagesBoxRef.current;
    if (!el || messages.length === 0) return;
    if (justOpenedRef.current) {
      // First load after opening a chat — jump to bottom instantly
      messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
      justOpenedRef.current = false;
      setShowScrollBtn(false);
      return;
    }
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distFromBottom < 120) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      setShowScrollBtn(false);
    }
  }, [messages]);

  const openSession = (s: Session) => {
    userScrolledUp.current = false;
    justOpenedRef.current = true;
    setShowScrollBtn(false);
    setActive(s);
    setMessages([]);
    setReply("");
    setGuestInfo(null);
    setShowInfo(window.innerWidth > 768);
    setCmdPanel(null);
    setSelectedTemplate(null);
    setShowSearch(false);
    setThreadSearch("");
    setImageFile(null);
    setImagePreviewUrl(null);
    setTranslations({});
    setShowTagPicker(false);
    setNewNoteText("");
    fetchGuestInfo(s.session_id);
  };

  const sendReply = async () => {
    if (!active || sending) return;
    // If a template is selected, send via blast-template API
    if (selectedTemplate) {
      setSending(true);
      const tpl = selectedTemplate;
      setSelectedTemplate(null);
      setReply("");
      try {
        await axios.post(`${API}/api/chat/blast-template`, {
          templateName: tpl.name,
          language: tpl.language,
          variables: { "1": active.guest_name || "" },
          recipients: [{ phone: active.session_id, name: active.guest_name || "" }],
          bodyText: tpl.bodyText,
        });
        // Refresh messages to show the sent template
        await fetchMessages(active.session_id);
      } catch {}
      finally { setSending(false); }
      return;
    }
    if (!reply.trim()) return;
    const text = reply.trim();
    setSending(true);
    setReply("");
    try {
      const body: Record<string, unknown> = { content: text, sentBy: staffName };
      const { data: msg } = await axios.post(
        `${API}/api/chat/sessions/${active.session_id}/messages`,
        body
      );
      userScrolledUp.current = false;
      setMessages(prev => [...prev, msg]);
    } catch { setReply(text); }
    finally { setSending(false); }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = ev => setImagePreviewUrl(ev.target?.result as string);
    reader.readAsDataURL(file);
    // Clear the input so the same file can be re-selected
    e.target.value = "";
  };

  const cancelImagePreview = () => {
    setImageFile(null);
    setImagePreviewUrl(null);
  };

  const sendImage = async () => {
    if (!imageFile || !active || uploadingImage) return;
    setUploadingImage(true);
    try {
      // Upload to Supabase via backend
      const formData = new FormData();
      formData.append("file", imageFile);
      formData.append("session_id", active.session_id);
      const { data: uploadData } = await axios.post(`${API}/api/chat/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const mediaUrl = uploadData.url;
      // Send the message
      const { data: msg } = await axios.post(
        `${API}/api/chat/sessions/${active.session_id}/messages`,
        { type: "image", media_url: mediaUrl, caption: "", sentBy: staffName }
      );
      userScrolledUp.current = false;
      setMessages(prev => [...prev, msg]);
      cancelImagePreview();
    } catch (e: any) {
      console.error("Image send failed:", e.message);
    } finally {
      setUploadingImage(false);
    }
  };

  const toggleStatus = async () => {
    if (!active) return;
    const newStatus = active.status === "open" ? "resolved" : "open";
    try {
      await axios.patch(`${API}/api/chat/sessions/${active.session_id}`, { status: newStatus });
      const updated = { ...active, status: newStatus as Session["status"] };
      setActive(updated);
      setSessions(prev => prev.map(s => s.session_id === active.session_id ? updated : s));
    } catch { }
  };

  const toggleAi = async () => {
    if (!active) return;
    const newVal = active.ai_enabled === false ? true : false;
    try {
      await axios.patch(`${API}/api/chat/sessions/${active.session_id}`, { ai_enabled: newVal });
      const updated = { ...active, ai_enabled: newVal };
      setActive(updated);
      setSessions(prev => prev.map(s => s.session_id === active.session_id ? updated : s));
    } catch { }
  };

  const translateMsg = async (key: string, text: string) => {
    if (translations[key]) {
      setTranslations(prev => { const n = { ...prev }; delete n[key]; return n; });
      return;
    }
    setTranslatingIds(prev => new Set(prev).add(key));
    try {
      const { data } = await axios.get(`${API}/api/chat/translate`, { params: { text } });
      setTranslations(prev => ({ ...prev, [key]: data.translated }));
    } catch { }
    finally { setTranslatingIds(prev => { const n = new Set(prev); n.delete(key); return n; }); }
  };

  const toggleTag = async (tag: string) => {
    if (!active) return;
    const current = active.tags || [];
    const updated = current.includes(tag) ? current.filter(t => t !== tag) : [...current, tag];
    try {
      await axios.patch(`${API}/api/chat/sessions/${active.session_id}`, { tags: updated });
      const newActive = { ...active, tags: updated };
      setActive(newActive);
      setSessions(prev => prev.map(s => s.session_id === active.session_id ? newActive : s));
    } catch { }
  };

  const handleHandoff = async () => {
    if (!active || active.status !== "needs_attention") return;
    try {
      // Keep handoff_reason so metrics can count "was handled" sessions
      await axios.patch(`${API}/api/chat/sessions/${active.session_id}`, {
        status: "open",
        ai_enabled: true,
        handoff_briefing: null,
        handoff_briefing_at: null,
      });
      const updated = { ...active, status: "open" as Session["status"], ai_enabled: true, handoff_briefing: null, handoff_briefing_at: null };
      setActive(updated);
      setSessions(prev => prev.map(s => s.session_id === active.session_id ? updated : s));
    } catch { }
  };

  const handleToggleInfo = () => {
    if (!showInfo && active && !guestInfo) fetchGuestInfo(active.session_id);
    setShowInfo(v => !v);
  };

  const filtered = sessions.filter(s => {
    const matchFilter =
      filter === "all"         ? true :
      filter === "needs_reply" ? (s.last_message_role === "user" && s.status === "open") :
      s.status === filter;
    const matchSearch = !search.trim() ||
      (s.guest_name || "").toLowerCase().includes(search.toLowerCase()) ||
      (s.guest_phone || "").includes(search);
    return matchFilter && matchSearch;
  });

  // Close filter dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (filterDdRef.current && !filterDdRef.current.contains(e.target as Node)) setShowFilterDd(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const openNewChatModal = async () => {
    setShowNewChat(true);
    setNcSearch("");
    setNcLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/dashboard/guests`);
      setNcGuests(data || []);
    } catch { }
    finally { setNcLoading(false); }
  };

  const handleStartChat = async (guest: { name: string; phoneNumber: string }) => {
    const phone = guest.phoneNumber.replace(/\D/g, "");
    setNcStarting(phone);
    try {
      const existing = sessions.find(s => s.session_id === phone || s.guest_phone === phone);
      if (existing) {
        openSession(existing);
        setShowNewChat(false);
        return;
      }
      const { data } = await axios.post(`${API}/api/chat/sessions`, { guestName: guest.name, guestPhone: phone });
      await fetchSessions();
      openSession(data.session as Session);
      setShowNewChat(false);
    } catch { }
    finally { setNcStarting(null); }
  };

  const totalUnread     = sessions.filter(s => s.status === "open").reduce((n, s) => n + (s.unread || 0), 0);
  const needsReplyCount = sessions.filter(s => s.last_message_role === "user" && s.status === "open").length;
  const attentionCount  = sessions.filter(s => s.status === "needs_attention").length;

  return (
    <DashboardLayout>
      <div className={`chat-page${active ? " thread-open" : ""}${theme === "light" ? " light-mode" : ""}`}>

        {/* ── LEFT: session list ── */}
        <div className="chat-sidebar">
          <div className="chat-sidebar-header">
            <h2 className="chat-sidebar-title">
              Live Chat
              {totalUnread > 0 && <span className="chat-unread-badge">{totalUnread}</span>}
            </h2>
            <div className="chat-header-controls">
              <label className="chat-bot-switch-label" title={botEnabled ? "Bot ON — click to disable" : "Bot OFF — click to enable"}>
                <span className="chat-bot-switch-text">{botEnabled ? "Bot" : "Bot"}</span>
                <span className="chat-bot-switch">
                  <input type="checkbox" checked={botEnabled} onChange={toggleBot} disabled={togglingBot} />
                  <span className="chat-bot-switch-track" />
                </span>
              </label>
              <button className="chat-theme-btn" onClick={toggleTheme} title={theme === "dark" ? "Light mode" : "Dark mode"}>
                {theme === "dark" ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
                    <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                    <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* View toggle */}
          <div className="chat-view-tabs">
            <button className={`chat-view-tab ${view === "chat" ? "active" : ""}`} onClick={() => setView("chat")}>Chats</button>
            <button className={`chat-view-tab ${view === "contacts" ? "active" : ""}`} onClick={() => { setView("contacts"); if (contacts.length === 0) fetchContacts(); }}>Contacts</button>
            <button className={`chat-view-tab ${view === "metrics" ? "active" : ""}`} onClick={() => setView("metrics")}>Metrics</button>
          </div>

          {view === "metrics" ? (
            <div className="chat-metrics-scroll">
              <ChatMetricsPanel />
            </div>
          ) : view === "contacts" ? (
            <div className="chat-contacts-wrap">
              <div className="chat-search-wrap">
                <svg className="chat-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                </svg>
                <input
                  className="chat-search-input"
                  placeholder="Search contacts…"
                  value={contactSearch}
                  onChange={e => setContactSearch(e.target.value)}
                />
              </div>
              <div className="chat-contacts-list">
                {contactsLoading ? (
                  <div className="chat-contacts-empty">Loading…</div>
                ) : (() => {
                  const q = contactSearch.toLowerCase();
                  const filtered = contacts.filter(c =>
                    (c.name || "").toLowerCase().includes(q) ||
                    (c.phoneNumber || "").includes(q) ||
                    (c.email || "").toLowerCase().includes(q)
                  );
                  if (filtered.length === 0) return <div className="chat-contacts-empty">No contacts found</div>;
                  return filtered.map(c => {
                    const phone = String(c.phoneNumber || "").replace(/\D/g, "");
                    const hasSession = sessions.some(s => s.session_id === phone || s.guest_phone === phone);
                    const col = avatarColor(c.name);
                    const initials = (c.name || "?").split(" ").map((w: string) => w[0]).join("").slice(0, 2).toUpperCase();
                    return (
                      <button
                        key={c.id}
                        className="chat-contact-row"
                        onClick={() => handleStartChat({ name: c.name, phoneNumber: String(c.phoneNumber || "") })}
                      >
                        <div className="chat-contact-avatar" style={{ background: col.bg, color: col.fg }}>{initials}</div>
                        <div className="chat-contact-info">
                          <span className="chat-contact-name">{c.name || "—"}</span>
                          <span className="chat-contact-phone">{c.phoneNumber || "—"}</span>
                        </div>
                        {hasSession && <span className="chat-contact-active-dot" title="Active chat" />}
                      </button>
                    );
                  });
                })()}
              </div>
            </div>
          ) : (
          <>

          {/* Search */}
          <div className="chat-search-wrap">
            <svg className="chat-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              className="chat-search-input"
              placeholder="Search guests…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {/* New Chat + Blast buttons */}
          <div className="chat-new-chat-wrap">
            <button className="chat-new-chat-btn" onClick={openNewChatModal}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                <line x1="12" y1="8" x2="12" y2="12"/><line x1="10" y1="10" x2="14" y2="10"/>
              </svg>
              New Conversation
            </button>
            <button className="chat-blast-btn" onClick={openBlastModal} title="Send WhatsApp template to guests">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
              Send Template
            </button>
          </div>

          {/* Filter tabs */}
          <div className="chat-filter-row">
            <div className="chat-filter-primary">
              {([
                { key: "all",             label: "All",     icon: null, count: sessions.length },
                { key: "needs_reply",     label: "Reply",   icon: "↩",  count: needsReplyCount },
                { key: "needs_attention", label: "Handoff", icon: "⚠",  count: attentionCount },
              ] as const).map(({ key, label, icon, count }) => (
                <button key={key} className={`chat-filter-btn ${filter === key ? "active" : ""}`} onClick={() => setFilter(key)}>
                  {icon && <span className="chat-filter-icon">{icon}</span>}
                  {label}
                  {count > 0 && <span className="chat-filter-count">{count}</span>}
                </button>
              ))}
            </div>
            <div className="chat-filter-dropdown-wrap" ref={filterDdRef}>
              <button
                className={`chat-filter-more ${(filter === "open" || filter === "resolved") ? "active" : ""}`}
                onClick={() => setShowFilterDd(v => !v)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12">
                  <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/>
                </svg>
                {filter === "open" ? "Open" : filter === "resolved" ? "Resolved" : "Filter"}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="10" height="10">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
              {showFilterDd && (
                <div className="chat-filter-dropdown">
                  {([
                    { key: "open",     label: "Open",     count: sessions.filter(s => s.status === "open").length },
                    { key: "resolved", label: "Resolved", count: sessions.filter(s => s.status === "resolved").length },
                  ] as const).map(({ key, label, count }) => (
                    <button key={key} className={`chat-filter-dropdown-item ${filter === key ? "active" : ""}`}
                      onClick={() => { setFilter(key); setShowFilterDd(false); }}>
                      {label}
                      {count > 0 && <span className="chat-filter-count">{count}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="chat-session-list">
            {loadingSessions && <div className="chat-empty">Loading…</div>}
            {!loadingSessions && filtered.length === 0 && (
              <div className="chat-empty-zero">
                <div className="chat-empty-zero-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="28" height="28">
                    <rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="2 8 12 14 22 8"/>
                  </svg>
                </div>
                <p className="chat-empty-zero-title">You're all caught up</p>
                <p className="chat-empty-zero-sub">
                  {filter === "all" ? "No conversations yet" :
                   filter === "needs_reply" ? "No guests waiting for a reply" :
                   filter === "needs_attention" ? "No handoffs need attention" :
                   filter === "open" ? "No open conversations" :
                   "No resolved conversations"}
                </p>
              </div>
            )}
            {[...filtered]
              .sort((a, b) => {
                // Pinned always first
                const pa = pinnedIds.has(a.session_id) ? 0 : 1;
                const pb = pinnedIds.has(b.session_id) ? 0 : 1;
                if (pa !== pb) return pa - pb;
                const wa = waitingInfo(a), wb = waitingInfo(b);
                const ra = wa?.level === "red" ? 0 : wa?.level === "amber" ? 1 : 2;
                const rb = wb?.level === "red" ? 0 : wb?.level === "amber" ? 1 : 2;
                if (ra !== rb) return ra - rb;
                // Tiebreak: most recent message first
                const ta = new Date(a.last_message_at || a.updated_at).getTime();
                const tb = new Date(b.last_message_at || b.updated_at).getTime();
                return tb - ta;
              })
              .map(s => {
                const wait = waitingInfo(s);
                const av = avatarColor(s.guest_name);
                return (
                  <div key={s.session_id} className="chat-session-item-wrap">
                  <button
                    className={`chat-session-item ${active?.session_id === s.session_id ? "active" : ""} ${wait ? `waiting-${wait.level}` : ""} ${flashIds.has(s.session_id) ? "chat-session-flash" : ""}`}
                    onClick={() => openSession(s)}
                  >
                    <div className="chat-avatar-wrap">
                      <div
                        className="chat-session-avatar"
                        style={{ background: av.bg, color: av.fg }}
                      >
                        {(s.guest_name?.[0] ?? "?").toUpperCase()}
                      </div>
                      {s.source === "whatsapp" && (
                        <span className="chat-channel-badge chat-channel-whatsapp"><WaIcon /></span>
                      )}
                      {s.source === "website" && (
                        <span className="chat-channel-badge chat-channel-website">
                          <svg viewBox="0 0 24 24" fill="currentColor" width="9" height="9"><circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" fill="none" stroke="#fff" strokeWidth="1.5"/><path d="M2 12h20" stroke="#fff" strokeWidth="1.5" fill="none"/></svg>
                        </span>
                      )}
                    </div>
                    <div className="chat-session-info">
                      <div className="chat-session-top">
                        <div className="chat-session-name-row">
                          <span className={`chat-session-name ${s.unread > 0 ? "chat-session-name--unread" : ""}`}>
                            {s.guest_name || "Guest"}
                          </span>
                          {s.guest_status === "arriving" && (
                            <span className="chat-guest-chip arriving">Arriving</span>
                          )}
                          {s.guest_status === "inhouse" && (
                            <span className="chat-guest-chip inhouse">
                              {s.room_number ? `Room ${s.room_number}` : "In House"}
                            </span>
                          )}
                        </div>
                        {wait ? (
                          <span className="chat-session-wait-label">{fmtWait(wait.mins)}</span>
                        ) : (
                          <span className="chat-session-time">{timeAgo(s.last_message_at || s.updated_at)}</span>
                        )}
                      </div>
                      <div className={`chat-session-preview ${s.last_message_role === "user" ? "preview-needs-reply" : ""}`}>
                        {(() => {
                          const media = s.last_message ? parseMediaContent(s.last_message) : null;
                          if (media) {
                            const svgMap: Record<string, React.ReactElement> = {
                              image: <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" width="12" height="12" style={{flexShrink:0}}><rect x="2" y="4" width="16" height="12" rx="2"/><circle cx="7" cy="8.5" r="1.5"/><polyline points="2 15 7 10 11 14 14 11 18 15"/></svg>,
                              audio: <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" width="12" height="12" style={{flexShrink:0}}><path d="M8 4h4v12H8z"/><path d="M4 7v6a6 6 0 0 0 12 0V7"/></svg>,
                              video: <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" width="12" height="12" style={{flexShrink:0}}><rect x="2" y="5" width="12" height="10" rx="1.5"/><polyline points="14 8.5 18 6 18 14 14 11.5"/></svg>,
                              document: <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" width="12" height="12" style={{flexShrink:0}}><path d="M5 2h7l4 4v12H4V2z"/><polyline points="12 2 12 6 16 6"/></svg>,
                            };
                            const labels: Record<string, string> = { image: "Photo", audio: "Voice message", video: "Video", document: "File" };
                            const prefix = s.last_message_role === "assistant" ? "AI: " : s.last_message_role === "staff" ? "You: " : "";
                            return (
                              <span style={{ display: "flex", alignItems: "center", gap: 4, opacity: 0.75 }}>
                                {svgMap[media.type] ?? svgMap.document}
                                {prefix}{labels[media.type] ?? "Media"}
                              </span>
                            );
                          }
                          return previewText(s.last_message, s.last_message_role);
                        })()}
                      </div>
                      {s.tags && s.tags.length > 0 && (
                        <div className="chat-session-tags">
                          {s.tags.slice(0, 2).map(tag => (
                            <span key={tag} className="chat-tag">{tag}</span>
                          ))}
                          {s.tags.length > 2 && <span className="chat-tag-more">+{s.tags.length - 2}</span>}
                        </div>
                      )}
                    </div>
                    {s.unread > 0 && <span className="chat-session-unread">{s.unread}</span>}
                  </button>
                  <button
                    className={`chat-session-pin-btn${pinnedIds.has(s.session_id) ? " pinned" : ""}`}
                    title={pinnedIds.has(s.session_id) ? "Unpin" : "Pin to top"}
                    onClick={e => { e.stopPropagation(); togglePin(s.session_id); }}
                  >
                    <svg viewBox="0 0 24 24" fill={pinnedIds.has(s.session_id) ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" width="12" height="12">
                      <path d="M12 2l2.4 6.4L21 10l-4.8 4.4 1.4 6.6L12 18l-5.6 3 1.4-6.6L3 10l6.6-1.6z"/>
                    </svg>
                  </button>
                  <button
                    className="chat-session-delete-btn"
                    title="Archive conversation"
                    onClick={e => { e.stopPropagation(); deleteSession(s.session_id); }}
                  >
                    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" width="13" height="13">
                      <line x1="4" y1="4" x2="16" y2="16"/><line x1="16" y1="4" x2="4" y2="16"/>
                    </svg>
                  </button>
                  </div>
                );
              })
            }
          </div>

          </>
          )}
        </div>

        {/* ── CENTER: message thread ── */}
        {!active ? (
          <div className="chat-empty-state">
            <div className="chat-empty-state-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" width="38" height="38">
                <rect x="2" y="4" width="20" height="16" rx="2.5"/>
                <polyline points="2 8 12 14 22 8"/>
              </svg>
            </div>
            <p className="chat-empty-title">Select a conversation</p>
            <p className="chat-empty-sub">Pick a guest from the list to view messages</p>
          </div>
        ) : (
          <div className="chat-thread">

            {active.status === "needs_attention" && active.handoff_briefing && (
              <div className="chat-handoff-banner">
                <span className="chat-handoff-banner-label">⚠ Handoff — {active.handoff_reason || "operator needed"}</span>
                <p className="chat-handoff-banner-briefing">{active.handoff_briefing}</p>
              </div>
            )}
            <div className="chat-thread-header">
              <button className="chat-back-btn" onClick={() => setActive(null)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="13" height="13">
                  <polyline points="15 18 9 12 15 6"/>
                </svg>
                Back
              </button>
              <button
                className={`chat-thread-search-btn ${showSearch ? "active" : ""}`}
                onClick={() => { setShowSearch(v => !v); if (showSearch) setThreadSearch(""); }}
                title="Search messages"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
                  <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                </svg>
              </button>
              <div className="chat-thread-avatar">
                {(active.guest_name?.[0] ?? "?").toUpperCase()}
              </div>
              <div className="chat-thread-info">
                {renaming ? (
                  <div className="chat-rename-row">
                    <input
                      ref={renameInputRef}
                      className="chat-rename-input"
                      value={renameVal}
                      onChange={e => setRenameVal(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setRenaming(false); }}
                      placeholder="Guest name…"
                    />
                    <button className="chat-rename-save" onClick={commitRename}>Save</button>
                    <button className="chat-rename-cancel" onClick={() => setRenaming(false)}>✕</button>
                  </div>
                ) : (
                  <p className="chat-thread-name" onClick={startRename} title="Click to rename">
                    {active.guest_name || "Guest"}
                    <span className="chat-rename-pencil">✎</span>
                  </p>
                )}
                <p className="chat-thread-meta">
                  {active.guest_phone && <span>{active.guest_phone}</span>}
                  <span> · via {active.source}</span>
                </p>
                <div className="chat-thread-tags-row">
                  {(active.tags || []).map(tag => (
                    <span key={tag} className="chat-tag">
                      {tag}
                      <button className="chat-tag-remove" onClick={() => toggleTag(tag)}>✕</button>
                    </span>
                  ))}
                  <div style={{ position: "relative" }} ref={tagPickerRef}>
                    <button className="chat-tag-add-btn" onClick={() => setShowTagPicker(v => !v)} title="Add tag">+</button>
                    {showTagPicker && (
                      <div className="chat-tag-picker">
                        {PRESET_TAGS.filter(t => !(active.tags || []).includes(t)).map(tag => (
                          <button key={tag} className="chat-tag-picker-item" onClick={() => { toggleTag(tag); setShowTagPicker(false); }}>{tag}</button>
                        ))}
                        <form onSubmit={e => {
                          e.preventDefault();
                          const input = (e.currentTarget.elements.namedItem("customTag") as HTMLInputElement);
                          const val = input.value.trim();
                          if (val) { toggleTag(val); setShowTagPicker(false); input.value = ""; }
                        }} className="chat-tag-custom-form">
                          <input name="customTag" className="chat-tag-custom-input" placeholder="Custom tag…" autoFocus />
                          <button type="submit" className="chat-tag-custom-add">Add</button>
                        </form>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="chat-thread-actions">
                <button
                  className="chat-info-toggle"
                  onClick={handleToggleInfo}
                  title="Guest info"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
                  </svg>
                  <span className="chat-info-label">Info</span>
                </button>
                <button
                  className={`chat-bot-toggle ${active.ai_enabled !== false ? "bot-on" : "bot-off"}`}
                  onClick={toggleAi}
                  title="Toggle AI for this guest"
                >
                  <span className="chat-bot-dot" />
                  <span className="chat-bot-label">{active.ai_enabled !== false ? "AI ON" : "AI OFF"}</span>
                </button>
                {active.status === "needs_attention" && (
                  <button
                    className="chat-status-btn chat-btn-handle"
                    onClick={handleHandoff}
                    title="Mark handoff as handled — removes from Handoff queue"
                  >
                    <span className="chat-status-label">✓ Handled</span>
                  </button>
                )}
                <button
                  className={`chat-status-btn ${active.status === "open" || active.status === "needs_attention" ? "chat-btn-resolve" : "chat-btn-reopen"}`}
                  onClick={toggleStatus}
                >
                  <span className="chat-status-label">{active.status === "open" || active.status === "needs_attention" ? "Mark Resolved" : "Reopen"}</span>
                </button>
              </div>
            </div>

            {showSearch && (
              <div className="chat-thread-search-bar">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14" className="chat-thread-search-icon">
                  <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                </svg>
                <input
                  className="chat-thread-search-input"
                  placeholder="Search messages…"
                  value={threadSearch}
                  onChange={e => setThreadSearch(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter") { e.preventDefault(); gotoMatch(e.shiftKey ? -1 : 1); }
                    if (e.key === "Escape") { setThreadSearch(""); setShowSearch(false); }
                  }}
                  autoFocus
                />
                {threadSearch && (
                  <span className="chat-thread-search-count">
                    {searchMatches.length === 0 ? "0" : `${searchIdx + 1}/${searchMatches.length}`}
                  </span>
                )}
                <div className="chat-thread-search-nav">
                  <button
                    className="chat-thread-search-arrow"
                    onClick={() => gotoMatch(-1)}
                    disabled={searchMatches.length === 0}
                    title="Previous match"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" width="14" height="14"><polyline points="18 15 12 9 6 15"/></svg>
                  </button>
                  <button
                    className="chat-thread-search-arrow"
                    onClick={() => gotoMatch(1)}
                    disabled={searchMatches.length === 0}
                    title="Next match"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" width="14" height="14"><polyline points="6 9 12 15 18 9"/></svg>
                  </button>
                </div>
                <button className="chat-thread-search-clear" onClick={() => { setThreadSearch(""); setShowSearch(false); }}>
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13"><line x1="2" y1="2" x2="14" y2="14"/><line x1="14" y1="2" x2="2" y2="14"/></svg>
                </button>
              </div>
            )}

            <div style={{ position: "relative", flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
              <div
                className="chat-messages"
                ref={messagesBoxRef}
                onScroll={() => {
                  const el = messagesBoxRef.current;
                  if (!el) return;
                  const scrolledUp = el.scrollHeight - el.scrollTop - el.clientHeight > 80;
                  userScrolledUp.current = scrolledUp;
                  setShowScrollBtn(scrolledUp);
                }}
              >
              {/* Loading only on first fetch — not on every 4s poll */}
              {loadingMsgs && messages.length === 0 && <div className="chat-loading">Loading messages…</div>}
              {!loadingMsgs && messages.length === 0 && (
                <div className="chat-no-messages">No messages yet</div>
              )}
              {displayItems.map((item, i) => {
                const role = item.kind === "album" ? item.role : item.msg.role;
                const sentBy = item.kind === "album" ? item.sent_by : item.msg.sent_by;
                const createdAt = item.kind === "album" ? item.created_at : item.msg.created_at;
                const key = item.kind === "album" ? `album-${item.msgs[0].id}` : String(item.msg.id);

                const prevItem = i > 0 ? displayItems[i - 1] : null;
                const nextItem = i < displayItems.length - 1 ? displayItems[i + 1] : null;
                const prevRole = prevItem ? (prevItem.kind === "album" ? prevItem.role : prevItem.msg.role) : null;
                const prevSentBy = prevItem ? (prevItem.kind === "album" ? prevItem.sent_by : prevItem.msg.sent_by) : null;
                const prevCreatedAt = prevItem ? (prevItem.kind === "album" ? prevItem.created_at : prevItem.msg.created_at) : null;
                const nextRole = nextItem ? (nextItem.kind === "album" ? nextItem.role : nextItem.msg.role) : null;
                const nextSentBy = nextItem ? (nextItem.kind === "album" ? nextItem.sent_by : nextItem.msg.sent_by) : null;
                const nextCreatedAt = nextItem ? (nextItem.kind === "album" ? nextItem.created_at : nextItem.msg.created_at) : null;

                const showDate = i === 0 || !sameDay(prevCreatedAt!, createdAt);
                const sameAsPrev = !showDate && prevRole === role && (role !== "staff" || prevSentBy === sentBy);
                const sameAsNext = nextRole && nextCreatedAt && sameDay(createdAt, nextCreatedAt) && nextRole === role && (role !== "staff" || nextSentBy === sentBy);
                const showAvatar = !sameAsNext;
                const grouped = !!sameAsPrev;

                const renderAvatar = (r: string) => {
                  if (r === "user") return (
                    <div className="msg-avatar msg-avatar-user" style={{ visibility: showAvatar ? "visible" : "hidden" }}>
                      {(active?.guest_name?.[0] ?? "?").toUpperCase()}
                    </div>
                  );
                  if (r === "assistant") return (
                    <div className="msg-avatar msg-avatar-assistant" style={{ visibility: showAvatar ? "visible" : "hidden" }}>
                      <svg viewBox="0 0 24 24" fill="currentColor" width="15" height="15">
                        <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7v1a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3v-1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73A2 2 0 0 1 12 2zm-3 12a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm6 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"/>
                      </svg>
                    </div>
                  );
                  if (r === "note") return null;
                  return (
                    <div className="msg-avatar msg-avatar-staff" style={{ visibility: showAvatar ? "visible" : "hidden" }}>
                      <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                        <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
                      </svg>
                    </div>
                  );
                };

                if (item.kind === "album") {
                  const albumImgs = item.msgs.map(m => {
                    const med = parseMediaContent(m.content);
                    return med ? { url: med.url, caption: med.caption } : null;
                  }).filter(Boolean) as { url: string; caption?: string }[];
                  return (
                    <div key={key} className={grouped ? "msg-grouped" : ""}>
                      {showDate && <div className="chat-date-sep"><span>{fmtDate(createdAt)}</span></div>}
                      <div className={`chat-bubble-row chat-bubble-${role}`}>
                        {role === "user" && renderAvatar(role)}
                        <div className={`chat-bubble chat-bubble-inner-${role} chat-bubble-album`}>
                          <AlbumGrid msgs={albumImgs} onImageClick={openLightbox} />
                          <span className="chat-bubble-time">{fmtTime(createdAt)}</span>
                        </div>
                        {role !== "user" && renderAvatar(role)}
                      </div>
                    </div>
                  );
                }

                const msg = item.msg;

                // Skip internal notes — they are shown in the guest info panel instead
                if (role === "note") return null;

                const msgKey = String(msg.id);
                const isActiveMatch = threadSearch.trim() && searchMatches[searchIdx] === msg.id;
                return (
                  <div key={key} className={grouped ? "msg-grouped" : ""} id={`msg-${msg.id}`}>
                    {showDate && <div className="chat-date-sep"><span>{fmtDate(createdAt)}</span></div>}
                    <div className={`chat-bubble-row chat-bubble-${role}`}>
                      {role === "user" && renderAvatar(role)}
                      <div className={`chat-bubble chat-bubble-inner-${role}${isActiveMatch ? " chat-bubble-match" : ""}`}>
                        {role === "staff" && msg.sent_by && !grouped && (
                          <span className="chat-sent-by">{msg.sent_by}</span>
                        )}
                        {(() => {
                          const media = parseMediaContent(msg.content);
                          if (media) return <MessageMedia media={media} onImageClick={openLightbox} />;
                          const txt = translations[msgKey] || msg.content;
                          return <p>{threadSearch.trim() ? highlightText(txt, threadSearch) : txt}</p>;
                        })()}
                        {role === "user" && !parseMediaContent(msg.content) && hasNonLatin(msg.content) && (
                          <div>
                            <button
                              className="chat-translate-btn"
                              onClick={() => translateMsg(msgKey, msg.content)}
                              disabled={translatingIds.has(msgKey)}
                            >
                              {translatingIds.has(msgKey) ? "Translating…" : translations[msgKey] ? "Show original" : "Translate"}
                            </button>
                          </div>
                        )}
                        <span className="chat-bubble-time">
                          {fmtTime(createdAt)}
                          {role === "staff" && (
                            <span className="chat-wa-tick">
                              {!msg.wa_message_id && !msg.wa_status ? (
                                <span title="Sending…">⏱</span>
                              ) : msg.wa_status === "read" ? (
                                <span title="Read" style={{ color: "#C9A84C" }}>✓✓</span>
                              ) : msg.wa_status === "delivered" ? (
                                <span title="Delivered" style={{ color: "rgba(242,235,217,0.45)" }}>✓✓</span>
                              ) : msg.wa_status === "sent" ? (
                                <span title="Sent" style={{ color: "rgba(242,235,217,0.45)" }}>✓</span>
                              ) : msg.wa_status === "failed" ? (
                                <span title="Failed" style={{ color: "#ef4444" }}>!</span>
                              ) : null}
                            </span>
                          )}
                        </span>
                      </div>
                      {role !== "user" && renderAvatar(role)}
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

              {/* Scroll-to-bottom button — overlaid at center-bottom, outside scroll container */}
              {showScrollBtn && (
                <button
                  onClick={() => {
                    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
                    setShowScrollBtn(false);
                  }}
                  style={{
                    position: "absolute", bottom: 16, left: "50%", transform: "translateX(-50%)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    width: 36, height: 36, borderRadius: "50%",
                    background: "#C9A84C", border: "none", cursor: "pointer",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.5)", zIndex: 20, fontSize: 18, color: "#0d1117",
                  }}
                  aria-label="Scroll to bottom"
                >↓</button>
              )}
            </div>

            <div className={`chat-reply-bar ${active.status === "resolved" ? "chat-reply-disabled" : ""}`}>
              {active.status === "resolved" ? (
                <p className="chat-resolved-note">This conversation is resolved. Reopen to reply.</p>
              ) : (
                <>
                  {/* Image preview strip */}
                  {imagePreviewUrl && (
                    <div className="chat-image-preview-strip">
                      <img src={imagePreviewUrl} alt="Preview" className="chat-image-preview-thumb" />
                      <button className="chat-image-preview-cancel" onClick={cancelImagePreview} title="Remove image">&#x2715;</button>
                    </div>
                  )}
                  <div className="chat-reply-input-row" style={{ position: "relative" }}>
                    {/* Command shortcut panel */}
                    {cmdPanel && (
                      <div className="chat-cmd-panel" ref={cmdPanelRef}>
                        <div className="chat-cmd-header">
                          <span className="chat-cmd-title">
                            {cmdPanel === "guest" ? `Guest Details${active.guest_name ? ` — ${active.guest_name}` : ""}` :
                             cmdPanel === "hotel" ? "Hotel Information" :
                             "Send Template"}
                          </span>
                          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                            {cmdPanel === "hotel" && (
                              <button
                                style={{ background: "none", border: "none", cursor: "pointer", color: cmdManage ? "#C9A84C" : "rgba(242,235,217,0.35)", padding: "2px 4px", borderRadius: 4, display: "flex", alignItems: "center" }}
                                onClick={() => setCmdManage(v => !v)}
                                title="Manage"
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13">
                                  <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                                </svg>
                              </button>
                            )}
                            <button
                              style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(242,235,217,0.35)", padding: "2px 4px", borderRadius: 4, display: "flex", alignItems: "center", fontSize: 14 }}
                              onClick={() => { setCmdPanel(null); setCmdManage(false); }}
                            >
                              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
                                <line x1="2" y1="2" x2="14" y2="14"/><line x1="14" y1="2" x2="2" y2="14"/>
                              </svg>
                            </button>
                          </div>
                        </div>
                        <input
                          className="chat-cmd-search"
                          placeholder="Search…"
                          value={cmdSearch}
                          onChange={e => setCmdSearch(e.target.value)}
                          autoFocus
                        />
                        <div className="chat-cmd-list">
                          {/* Guest panel */}
                          {cmdPanel === "guest" && (
                            guestSnippets
                              .filter(s => !cmdSearch.trim() || s.title.toLowerCase().includes(cmdSearch.toLowerCase()) || s.text.toLowerCase().includes(cmdSearch.toLowerCase()))
                              .map((s, i) => (
                                <div key={i} className="chat-cmd-item" onClick={() => { setReply(s.text); setCmdPanel(null); setCmdSearch(""); }}>
                                  <div>
                                    <div className="chat-cmd-item-title">{s.title}</div>
                                    <div className="chat-cmd-item-preview">{s.text}</div>
                                  </div>
                                </div>
                              ))
                          )}
                          {/* Hotel panel */}
                          {cmdPanel === "hotel" && (
                            hotelFacts
                              .filter(f => !cmdSearch.trim() || f.title.toLowerCase().includes(cmdSearch.toLowerCase()) || f.text.toLowerCase().includes(cmdSearch.toLowerCase()))
                              .map(f => (
                                <div key={f.id} className="chat-cmd-item" style={{ justifyContent: "space-between" }}>
                                  <div style={{ flex: 1, cursor: "pointer" }} onClick={() => { setReply(f.text); setCmdPanel(null); setCmdSearch(""); }}>
                                    <div className="chat-cmd-item-title">{f.title}</div>
                                    <div className="chat-cmd-item-preview">{f.text.slice(0, 60)}{f.text.length > 60 ? "…" : ""}</div>
                                  </div>
                                  {cmdManage && (
                                    <button
                                      style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(242,235,217,0.3)", padding: "4px", flexShrink: 0 }}
                                      onClick={async () => {
                                        if (f.id.startsWith("cfg-")) { alert("This comes from your hotel policies — edit it in Settings → Policies."); return; }
                                        if (f.id.startsWith("faq-")) {
                                          try {
                                            const token = localStorage.getItem("ai2_token") || "";
                                            await axios.delete(`${API}/api/settings/faqs/${f.id.slice(4)}`, { headers: { Authorization: `Bearer ${token}` } });
                                          } catch { /* */ }
                                        }
                                        setHotelFacts(prev => prev.filter(x => x.id !== f.id));
                                      }}
                                      title="Delete"
                                    >
                                      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" width="11" height="11">
                                        <polyline points="2 4 14 4"/><path d="M5 4V2h6v2"/><rect x="3" y="4" width="10" height="10" rx="1"/>
                                      </svg>
                                    </button>
                                  )}
                                </div>
                              ))
                          )}
                          {/* Template panel */}
                          {cmdPanel === "template" && (
                            blastTplLoading ? (
                              <div style={{ padding: "12px 8px", color: "rgba(242,235,217,0.4)", fontSize: 12 }}>Loading templates…</div>
                            ) : blastTemplates.length === 0 ? (
                              <div style={{ padding: "12px 8px", color: "rgba(242,235,217,0.4)", fontSize: 12 }}>No approved templates found.</div>
                            ) : (
                              blastTemplates
                                .filter(t => !cmdSearch.trim() || t.name.toLowerCase().includes(cmdSearch.toLowerCase()) || t.bodyText.toLowerCase().includes(cmdSearch.toLowerCase()))
                                .map(t => (
                                  <div
                                    key={t.name}
                                    className="chat-cmd-item"
                                    onClick={() => {
                                      const body = t.bodyText.replace(/\{\{1\}\}/g, active.guest_name || "");
                                      setReply(body);
                                      setSelectedTemplate(t);
                                      setCmdPanel(null);
                                      setCmdSearch("");
                                    }}
                                  >
                                    <div>
                                      <div className="chat-cmd-item-title">{t.name}</div>
                                      <div className="chat-cmd-item-preview">{t.bodyText.slice(0, 60)}{t.bodyText.length > 60 ? "…" : ""}</div>
                                    </div>
                                  </div>
                                ))
                            )
                          )}
                        </div>
                        {/* Hotel manage form */}
                        {cmdPanel === "hotel" && cmdManage && (
                          <div className="chat-cmd-manage-form">
                            <input
                              className="chat-cmd-search"
                              style={{ margin: 0 }}
                              placeholder="Fact title"
                              value={newFactTitle}
                              onChange={e => setNewFactTitle(e.target.value)}
                            />
                            <textarea
                              className="chat-cmd-search"
                              style={{ margin: 0, resize: "none" }}
                              placeholder="Fact text"
                              value={newFactText}
                              onChange={e => setNewFactText(e.target.value)}
                              rows={2}
                            />
                            <button
                              style={{ alignSelf: "flex-end", padding: "4px 12px", background: "rgba(201,168,76,0.15)", border: "1px solid rgba(201,168,76,0.3)", borderRadius: 5, color: "#C9A84C", fontSize: 12, cursor: "pointer" }}
                              onClick={async () => {
                                const title = newFactTitle.trim(), text = newFactText.trim();
                                if (!title || !text) return;
                                let id = Date.now().toString();
                                try {
                                  const token = localStorage.getItem("ai2_token") || "";
                                  const { data } = await axios.post(`${API}/api/settings/faqs`,
                                    { faq_summary: title, faq_content: text, language: "English", active: true },
                                    { headers: { Authorization: `Bearer ${token}` } });
                                  if (data?.id != null) id = `faq-${data.id}`;
                                } catch { /* fall back to local-only */ }
                                setHotelFacts(prev => [...prev, { id, title, text }]);
                                setNewFactTitle("");
                                setNewFactText("");
                              }}
                            >Add</button>
                          </div>
                        )}
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,video/*"
                      style={{ display: "none" }}
                      onChange={handleFileSelect}
                    />

                    {/* Row 1: full-width text input */}
                    <textarea
                      className="chat-reply-input"
                      placeholder="Type a reply…  (Enter to send, Shift+Enter for new line)"
                      value={reply}
                      rows={2}
                      onChange={e => {
                        const val = e.target.value;
                        if (val === "/") { setCmdPanel("guest"); setCmdSearch(""); setReply(""); return; }
                        if (val === "#") { setCmdPanel("hotel"); setCmdSearch(""); setReply(""); return; }
                        if (val === "@") {
                          setCmdPanel("template");
                          setCmdSearch("");
                          setReply("");
                          if (blastTemplates.length === 0) {
                            setBlastTplLoading(true);
                            axios.get(`${API}/api/chat/templates`)
                              .then(r => setBlastTemplates(r.data?.templates || []))
                              .catch(() => {})
                              .finally(() => setBlastTplLoading(false));
                          }
                          return;
                        }
                        setReply(val);
                      }}
                      onKeyDown={e => {
                        if (e.key === "Escape") { setCmdPanel(null); return; }
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          if (imagePreviewUrl) sendImage();
                          else sendReply();
                        }
                      }}
                    />

                    {/* Row 2: actions — image, shortcut chips, send (full width) */}
                    <div className="chat-reply-actions">
                      <button
                        className="chat-image-pick-btn"
                        title="Send image"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingImage}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="17" height="17">
                          <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                        </svg>
                      </button>

                      <button
                        className={`chat-cmd-chip ${cmdPanel === "guest" ? "active" : ""}`}
                        onClick={() => { setCmdPanel(cmdPanel === "guest" ? null : "guest"); setCmdSearch(""); }}
                      >
                        <span className="chat-cmd-chip-key">/</span> Guest
                      </button>
                      <button
                        className={`chat-cmd-chip ${cmdPanel === "hotel" ? "active" : ""}`}
                        onClick={() => { setCmdPanel(cmdPanel === "hotel" ? null : "hotel"); setCmdSearch(""); }}
                      >
                        <span className="chat-cmd-chip-key">#</span> Hotel
                      </button>
                      <button
                        className={`chat-cmd-chip ${cmdPanel === "template" ? "active" : ""}`}
                        onClick={() => {
                          if (cmdPanel === "template") { setCmdPanel(null); return; }
                          setCmdPanel("template"); setCmdSearch("");
                          if (blastTemplates.length === 0) {
                            setBlastTplLoading(true);
                            axios.get(`${API}/api/chat/templates`)
                              .then(r => setBlastTemplates(r.data?.templates || []))
                              .catch(() => {})
                              .finally(() => setBlastTplLoading(false));
                          }
                        }}
                      >
                        <span className="chat-cmd-chip-key">@</span> Templates
                      </button>

                      {imagePreviewUrl ? (
                        <button className="chat-reply-send" disabled={uploadingImage} onClick={sendImage}>
                          {uploadingImage ? "Sending…" : "Send"}
                        </button>
                      ) : (
                        <button
                          className="chat-reply-send"
                          disabled={(!reply.trim() && !selectedTemplate) || sending}
                          onClick={sendReply}
                        >
                          {sending ? "Sending…" : "Send"}
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── RIGHT: guest info panel ── */}
        {active && showInfo && (
          <div className={`chat-info-panel info-open`}>
            <div className="chat-info-header">
              <span>Guest Profile</span>
              <button className="chat-info-close" onClick={() => setShowInfo(false)}>✕</button>
            </div>

            <div className="chat-info-scroll">
              {/* Avatar + identity */}
              <div className="chat-info-avatar-wrap">
                <div className="chat-info-avatar">
                  {(active.guest_name?.[0] ?? "?").toUpperCase()}
                </div>
                <p className="chat-info-name">{active.guest_name || "Guest"}</p>
                <p className="chat-info-phone">{active.guest_phone || "—"}</p>
                <div className="chat-info-badges">
                  <span className={`chat-source-badge chat-source-${active.source}`}>{active.source}</span>
                  {active.guest_status && (
                    <span className={`chat-source-badge chat-source-${active.guest_status}`}>
                      {active.guest_status === "inhouse" ? "In House" : "Arriving"}
                    </span>
                  )}
                  {active.room_number && (
                    <span className="chat-source-badge chat-source-room">
                      Room {active.room_number}
                    </span>
                  )}
                </div>
              </div>

              {loadingInfo ? (
                <div className="chat-info-loading">
                  <div className="chat-info-skeleton" /><div className="chat-info-skeleton" /><div className="chat-info-skeleton short" />
                </div>
              ) : !guestInfo ? null : (
                <>
                  {/* Stay stats */}
                  {guestInfo.stats && (
                    <div className="chat-info-stats-grid">
                      <div className="chat-info-stat">
                        <span className="chat-info-stat-val">{guestInfo.stats.totalStays}</span>
                        <span className="chat-info-stat-lbl">Stays</span>
                      </div>
                      <div className="chat-info-stat">
                        <span className="chat-info-stat-val">{guestInfo.stats.totalNights}</span>
                        <span className="chat-info-stat-lbl">Nights</span>
                      </div>
                      <div className="chat-info-stat">
                        <span className="chat-info-stat-val">
                          {guestInfo.stats.totalSpent > 0 ? `${Math.round(guestInfo.stats.totalSpent).toLocaleString()}` : "—"}
                        </span>
                        <span className="chat-info-stat-lbl">Spent</span>
                      </div>
                    </div>
                  )}

                  {/* Current / latest booking */}
                  {guestInfo.booking && (
                    <div className="chat-info-section">
                      <p className="chat-info-section-title">Current Booking</p>
                      <div className="chat-info-card">
                        <div className="chat-info-row">
                          <span>Ref</span>
                          <span className="chat-info-val">#{String(guestInfo.booking.bookingid).slice(-6)}</span>
                        </div>
                        {guestInfo.booking.roomnumber && (
                          <div className="chat-info-row">
                            <span>Room</span>
                            <span className="chat-info-val">{guestInfo.booking.roomnumber}</span>
                          </div>
                        )}
                        <div className="chat-info-row">
                          <span>Check-in</span>
                          <span className="chat-info-val">{new Date(guestInfo.booking.checkin + "T12:00:00").toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" })}</span>
                        </div>
                        <div className="chat-info-row">
                          <span>Check-out</span>
                          <span className="chat-info-val">{new Date(guestInfo.booking.checkout + "T12:00:00").toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" })}</span>
                        </div>
                        <div className="chat-info-row">
                          <span>Nights</span>
                          <span className="chat-info-val">{guestInfo.booking.nights || "—"}</span>
                        </div>
                        {guestInfo.booking.total > 0 && (
                          <div className="chat-info-row">
                            <span>Total</span>
                            <span className="chat-info-val">{guestInfo.booking.total.toLocaleString()}</span>
                          </div>
                        )}
                        {guestInfo.booking.booking_type && (
                          <div className="chat-info-row">
                            <span>Type</span>
                            <span className="chat-info-val">{guestInfo.booking.booking_type}</span>
                          </div>
                        )}
                        <div className="chat-info-row">
                          <span>Status</span>
                          <span className={`chat-info-status chat-info-status--${guestInfo.booking.status}`}>
                            {guestInfo.booking.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Past bookings */}
                  {guestInfo.allBookings && guestInfo.allBookings.length > 1 && (
                    <div className="chat-info-section">
                      <p className="chat-info-section-title">Past Visits</p>
                      <div className="chat-info-past-list">
                        {guestInfo.allBookings.slice(1).map(b => (
                          <div key={b.bookingid} className="chat-info-past-row">
                            <div className="chat-info-past-dates">
                              <span>{new Date(b.checkin + "T12:00:00").toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"2-digit" })}</span>
                              <span className="chat-info-past-sep">→</span>
                              <span>{new Date(b.checkout + "T12:00:00").toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"2-digit" })}</span>
                            </div>
                            <div className="chat-info-past-meta">
                              {b.roomnumber && <span>Rm {b.roomnumber}</span>}
                              {b.nights > 0 && <span>{b.nights}n</span>}
                              <span className={`chat-info-status chat-info-status--${b.status}`}>{b.status}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {!guestInfo.booking && (
                    <p className="chat-info-no-booking">No bookings found for this guest.</p>
                  )}

                  {/* Staff Notes */}
                  <div className="chat-info-section">
                    <p className="chat-info-section-title">Staff Notes</p>
                    {(guestInfo?.notes || []).length === 0 && (
                      <p style={{ fontSize: 12, color: "rgba(242,235,217,0.3)", padding: "4px 0 8px", margin: 0 }}>No notes yet</p>
                    )}
                    <div className="chat-info-notes-list">
                      {(guestInfo?.notes || []).map(n => (
                        <div key={n.id} className="chat-info-note">
                          <p className="chat-info-note-text">{n.text}</p>
                          <div className="chat-info-note-meta">
                            <span>{n.author}</span>
                            <span>·</span>
                            <span>{new Date(n.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}</span>
                            <button
                              className="chat-info-note-del"
                              onClick={async () => {
                                const updated = (guestInfo.notes || []).filter(x => x.id !== n.id);
                                await axios.patch(`${API}/api/chat/sessions/${active!.session_id}`, { guest_notes: updated });
                                setGuestInfo(prev => prev ? { ...prev, notes: updated } : prev);
                              }}
                            >
                              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" width="11" height="11">
                                <polyline points="2 4 14 4"/><path d="M5 4V2h6v2"/><rect x="3" y="4" width="10" height="10" rx="1"/>
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="chat-info-note-form">
                      <textarea
                        className="chat-info-note-input"
                        placeholder="Add a note about this guest…"
                        rows={2}
                        value={newNoteText}
                        onChange={e => setNewNoteText(e.target.value)}
                      />
                      <button
                        className="chat-info-note-submit"
                        disabled={!newNoteText.trim()}
                        onClick={async () => {
                          const note = { id: Date.now().toString(), text: newNoteText.trim(), author: staffName || "Staff", created_at: new Date().toISOString() };
                          const updated = [...(guestInfo?.notes || []), note];
                          await axios.patch(`${API}/api/chat/sessions/${active!.session_id}`, { guest_notes: updated });
                          setGuestInfo(prev => prev ? { ...prev, notes: updated } : prev);
                          setNewNoteText("");
                        }}
                      >Save note</button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

      </div>

      {/* ── New Chat Modal ── */}
      {showNewChat && (
        <div className="ncp-overlay" onClick={() => setShowNewChat(false)}>
          <div className="ncp-modal" onClick={e => e.stopPropagation()}>
            <div className="ncp-header">
              <div className="ncp-header-left">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                <span>New Conversation</span>
              </div>
              <button className="ncp-close" onClick={() => setShowNewChat(false)}>✕</button>
            </div>
            <div className="ncp-search-wrap">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14" className="ncp-search-icon">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                className="ncp-search-input"
                placeholder="Search guest name or phone…"
                value={ncSearch}
                onChange={e => setNcSearch(e.target.value)}
                autoFocus
              />
            </div>
            <div className="ncp-list">
              {ncLoading ? (
                <div className="ncp-empty">Loading guests…</div>
              ) : (() => {
                const q = ncSearch.toLowerCase();
                const filtered = ncGuests.filter(g =>
                  (g.name || "").toLowerCase().includes(q) ||
                  (g.phoneNumber || "").includes(q) ||
                  (g.email || "").toLowerCase().includes(q)
                );
                if (filtered.length === 0) return <div className="ncp-empty">No guests found</div>;
                return filtered.slice(0, 50).map(g => {
                  const phone = g.phoneNumber.replace(/\D/g, "");
                  const hasSession = sessions.some(s => s.session_id === phone || s.guest_phone === phone);
                  const initials = (g.name || "?").split(" ").map((w: string) => w[0]).join("").slice(0, 2).toUpperCase();
                  return (
                    <button
                      key={g.id}
                      className="ncp-guest-row"
                      onClick={() => handleStartChat(g)}
                      disabled={ncStarting === phone}
                    >
                      <div className="ncp-avatar">{initials}</div>
                      <div className="ncp-guest-info">
                        <span className="ncp-guest-name">{g.name}</span>
                        <span className="ncp-guest-phone">{g.phoneNumber || "—"}</span>
                      </div>
                      {hasSession
                        ? <span className="ncp-badge active">Active</span>
                        : <span className="ncp-badge new">Start</span>
                      }
                    </button>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ── BLAST TEMPLATE MODAL ── */}
      {showBlast && (
        <div className="ncp-overlay" onClick={e => { if ((e.target as HTMLElement).classList.contains("ncp-overlay")) setShowBlast(false); }}>
          <div className="blast-modal">
            <div className="blast-header">
              <h3>Send WhatsApp Template</h3>
              <button className="ncp-close" onClick={() => setShowBlast(false)}>×</button>
            </div>

            {blastResults ? (
              /* ── Results view ── */
              <div className="blast-results">
                <p className="blast-results-title">Sent to {blastResults.filter(r => r.status === "sent").length} / {blastResults.length} guests</p>
                <div className="blast-results-list">
                  {blastResults.map((r, i) => (
                    <div key={i} className={`blast-result-row blast-result-${r.status}`}>
                      <span>{r.phone}</span>
                      <span className="blast-result-status">{r.status === "sent" ? "✓ Sent" : r.status === "skipped" ? "— Skipped" : `✗ ${r.reason || "Failed"}`}</span>
                    </div>
                  ))}
                </div>
                <button className="blast-send-btn" onClick={() => setShowBlast(false)} style={{ marginTop: 12 }}>Done</button>
              </div>
            ) : (
              <div className="blast-body">
                {blastTplError && <div className="blast-error">{blastTplError}</div>}

                {/* Step 1: Pick template */}
                <div className="blast-section-label">1 — Pick a template</div>
                {blastTplLoading ? (
                  <div className="blast-loading">Loading templates from Meta…</div>
                ) : blastTemplates.length === 0 ? (
                  <div className="blast-loading">No approved templates found. Make sure waba_id and access_token are set in hotel integrations.</div>
                ) : (
                  <div className="blast-tpl-list">
                    {blastTemplates.map(t => (
                      <button
                        key={t.name}
                        className={`blast-tpl-row ${blastSelected?.name === t.name ? "active" : ""}`}
                        onClick={() => { setBlastSelected(t); setBlastVars({}); }}
                      >
                        <span className="blast-tpl-name">{t.name}</span>
                        <span className="blast-tpl-lang">{t.language}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Template preview + variable inputs */}
                {blastSelected && (
                  <div className="blast-tpl-preview">
                    <div className="blast-preview-text">
                      {blastSelected.bodyText.split(/(\{\{\d+\}\})/).map((part, i) => {
                        const m = part.match(/^\{\{(\d+)\}\}$/);
                        if (m) {
                          const k = m[1];
                          return <span key={i} className="blast-var-chip">{blastVars[k] || `{{${k}}}`}</span>;
                        }
                        return <span key={i}>{part}</span>;
                      })}
                    </div>
                    {blastSelected.variables.filter(v => v !== "1").length > 0 && (
                      <div className="blast-var-inputs">
                        {blastSelected.variables.filter(v => v !== "1").map(v => (
                          <div key={v} className="blast-var-row">
                            <label>{`{{${v}}}`}</label>
                            <input
                              className="blast-var-input"
                              placeholder={`Variable ${v}`}
                              value={blastVars[v] || ""}
                              onChange={e => setBlastVars(prev => ({ ...prev, [v]: e.target.value }))}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                    {blastSelected.variables.includes("1") && (
                      <p className="blast-var-hint">Name <strong style={{ color: "#c9a84c" }}>{"{{1}}"}</strong> is filled per guest — edit names below</p>
                    )}
                  </div>
                )}

                {/* Step 2: Pick recipients */}
                {blastSelected && (
                  <>
                    <div className="blast-section-label" style={{ marginTop: 14 }}>2 — Choose recipients</div>
                    <div className="blast-tabs">
                      <button className={`blast-tab ${blastTab === "all" ? "active" : ""}`} onClick={() => { setBlastTab("all"); setBlastGuestSel(new Set()); }}>
                        All guests ({blastAllGuests.length})
                      </button>
                      <button className={`blast-tab ${blastTab === "new" ? "active" : ""}`} onClick={() => { setBlastTab("new"); setBlastGuestSel(new Set()); }}>
                        New ({blastGuests.length})
                      </button>
                      <button className={`blast-tab ${blastTab === "waiting" ? "active" : ""}`} onClick={() => { setBlastTab("waiting"); setBlastGuestSel(new Set()); }}>
                        Waiting 24h+ ({sessions.filter(s => s.last_message_role === "user" && s.status === "open" && (Date.now() - new Date(s.updated_at).getTime()) > 86400000).length})
                      </button>
                    </div>
                    <div className="blast-guest-toolbar">
                      <input
                        className="blast-guest-search"
                        placeholder="Search…"
                        value={blastGuestSearch}
                        onChange={e => setBlastGuestSearch(e.target.value)}
                      />
                      <button className="blast-sel-btn" onClick={selectAllBlast}>All</button>
                      <button className="blast-sel-btn" onClick={clearBlastSel}>None</button>
                    </div>
                    <div className="blast-guest-list">
                      {blastGuestsFiltered.length === 0
                        ? <div className="blast-loading">No guests in this category</div>
                        : blastGuestsFiltered.map(g => (
                          <div key={g.phone} className="blast-guest-row">
                            <input
                              type="checkbox"
                              checked={blastGuestSel.has(g.phone)}
                              onChange={() => toggleBlastGuest(g.phone)}
                            />
                            <div className="blast-guest-info">
                              <input
                                className="blast-name-edit"
                                value={blastNameOverrides[g.phone] ?? g.name}
                                onChange={e => setBlastNameOverrides(prev => ({ ...prev, [g.phone]: e.target.value }))}
                                placeholder="Guest name"
                              />
                              <span className="blast-guest-phone">{g.phone}</span>
                            </div>
                          </div>
                        ))
                      }
                    </div>

                    <button
                      className="blast-send-btn"
                      onClick={sendBlast}
                      disabled={blastSending || blastGuestSel.size === 0}
                    >
                      {blastSending ? "Sending…" : `Send to ${blastGuestSel.size} guest${blastGuestSel.size !== 1 ? "s" : ""}`}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Image lightbox ── */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          onTouchStart={e => { lbTouchX.current = e.touches[0].clientX; }}
          onTouchEnd={e => {
            const diff = lbTouchX.current - e.changedTouches[0].clientX;
            if (Math.abs(diff) > 50) {
              if (diff > 0) setLightbox(l => l && l.idx < l.images.length - 1 ? { ...l, idx: l.idx + 1 } : l);
              else          setLightbox(l => l && l.idx > 0                   ? { ...l, idx: l.idx - 1 } : l);
            }
          }}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.95)", zIndex: 9999, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}
        >
          {/* Close — fixed top-right, large tap target for mobile */}
          <button
            onClick={() => setLightbox(null)}
            style={{ position: "fixed", top: 14, right: 14, background: "rgba(255,255,255,0.12)", border: "none", color: "#fff", fontSize: 20, width: 44, height: 44, borderRadius: "50%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1 }}
          >✕</button>

          {/* Counter */}
          {lightbox.images.length > 1 && (
            <span style={{ position: "fixed", top: 22, left: "50%", transform: "translateX(-50%)", color: "rgba(255,255,255,0.55)", fontSize: 13, pointerEvents: "none", zIndex: 1 }}>
              {lightbox.idx + 1} / {lightbox.images.length}
            </span>
          )}

          {/* Image with overlaid prev/next tap zones */}
          <div onClick={e => e.stopPropagation()} style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", width: "100vw", flex: 1, paddingTop: 60, paddingBottom: 20 }}>
            <img
              src={lightbox.images[lightbox.idx].url}
              alt="Chat image"
              style={{ maxWidth: "96vw", maxHeight: "75vh", borderRadius: 8, objectFit: "contain", display: "block", userSelect: "none" }}
            />

            {/* Prev zone — left 30% of image area */}
            {lightbox.idx > 0 && (
              <button
                onClick={e => { e.stopPropagation(); setLightbox(l => l && ({ ...l, idx: l.idx - 1 })); }}
                style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "30%", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "flex-start", paddingLeft: 10 }}
              >
                <span style={{ background: "rgba(0,0,0,0.45)", color: "#fff", fontSize: 26, width: 38, height: 38, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>‹</span>
              </button>
            )}

            {/* Next zone — right 30% of image area */}
            {lightbox.idx < lightbox.images.length - 1 && (
              <button
                onClick={e => { e.stopPropagation(); setLightbox(l => l && ({ ...l, idx: l.idx + 1 })); }}
                style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: "30%", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "flex-end", paddingRight: 10 }}
              >
                <span style={{ background: "rgba(0,0,0,0.45)", color: "#fff", fontSize: 26, width: 38, height: 38, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>›</span>
              </button>
            )}
          </div>

          {/* Caption */}
          {lightbox.images[lightbox.idx].caption && (
            <p style={{ color: "rgba(255,255,255,0.65)", fontSize: 13, textAlign: "center", padding: "0 20px", marginBottom: 8 }}>
              {lightbox.images[lightbox.idx].caption}
            </p>
          )}

          {/* Dot indicators */}
          {lightbox.images.length > 1 && lightbox.images.length <= 12 && (
            <div onClick={e => e.stopPropagation()} style={{ display: "flex", gap: 7, paddingBottom: 20 }}>
              {lightbox.images.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setLightbox(l => l && ({ ...l, idx: i }))}
                  style={{ width: 8, height: 8, borderRadius: "50%", border: "none", padding: 0, cursor: "pointer", background: i === lightbox.idx ? "#C9A84C" : "rgba(255,255,255,0.28)", transition: "background 0.2s" }}
                />
              ))}
            </div>
          )}
        </div>
      )}

    </DashboardLayout>
  );
}
